// src/pages/AdminDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, query, orderBy, where, updateDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Users,
  User,
  TrendingUp,
  Calendar,
  DollarSign,
  Activity,
  Award,
  Clock,
  RefreshCw,
  ArrowLeft,
  Search,
  ChevronDown,
  ChevronUp,
  FileText,
  Upload,
  Trash2,
  History,
  CheckCircle2,
  XCircle,
  SkipForward,
  Link,
  UserPlus,
  BookOpen,
  Info,
  LogIn,
  Globe,
  Mail,
  KeyRound,
  Copy,
  Check,
  Pause,
  UserX,
  MapPin,
  Phone,
} from 'lucide-react';
import ReportCardUploadModal from '../components/ReportCardUploadModal';
import GeographicDistribution from '../components/GeographicDistribution';
import { getChildren } from '../utils/childrenHelper';
import { getGameName, getGameIcon } from '../utils/gameNames';
import { COURSE_PACKAGES } from '../config/coursePackages';
import { isSuperAdmin, isAdmin } from '../config/adminConfig';
import { resolveTimezone, formatLocalTime } from '../config/locationTimezones';
import {
  getBrowserTimezone,
  isoToDisplayDate,
  formatISOForDisplay,
  CALCULATOR_ZONES,
  COMMON_TIMEZONES,
  timeToUTC,
  timeFromUTC,
} from '../utils/timezoneUtils';

// ─── extractMeetingLink ─────────────────────────────────────────────────────
// Parses a pasted class-link input (which may be a bare URL, or an entire
// Zoom invite email) into a clean URL plus derived meeting ID / passcode.
// The passcode can only be recovered from literal "Passcode:"/"Password:"
// text in the paste — Zoom's `?pwd=` query value is an opaque encoded token,
// not the plaintext passcode.

function formatMeetingId(digits) {
  if (digits.length === 11) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  if (digits.length === 9) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return digits;
}

function extractMeetingLink(raw) {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) {
    return { value: null, meetingId: null, passcode: null, error: null };
  }

  const urlMatches = trimmed.match(/https?:\/\/\S+/g) || [];

  if (urlMatches.length === 0) {
    return {
      value: null,
      meetingId: null,
      passcode: null,
      error: 'No link found — paste the Zoom/meeting link.',
    };
  }

  if (urlMatches.length > 1) {
    return {
      value: null,
      meetingId: null,
      passcode: null,
      error: 'Found more than one link — paste just the single meeting link.',
    };
  }

  const value = urlMatches[0];
  const idMatch = value.match(/\/j\/(\d+)/);
  const meetingId = idMatch ? formatMeetingId(idMatch[1]) : null;
  const passcodeMatch = trimmed.match(/(?:passcode|password)\s*:?\s*(\S+)/i);
  const passcode = passcodeMatch ? (passcodeMatch[1].replace(/[^A-Za-z0-9]+$/, '') || null) : null;

  return { value, meetingId, passcode, error: null };
}

// ─── CopyableId ───────────────────────────────────────────────────────────────

function CopyableId({ label, value }) {
  const [copied, setCopied] = React.useState(false);
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-xs text-gray-400">{label}:</span>
      <span className="text-xs font-mono text-gray-500" title={value}>
        {value.length > 22 ? value.slice(0, 22) + '…' : value}
      </span>
      <button
        onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="text-gray-300 hover:text-gray-500 transition-colors"
        title="Copy to clipboard"
      >
        {copied ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
      </button>
    </span>
  );
}

// ─── Session History Component ────────────────────────────────────────────────
// Lazy-loads a child's session history from studentSessions/{childId}/sessions
// on first expand. Subsequent toggles use cached data.

function SessionHistory({ childId, childName, classes, userId }) {
  const [expanded, setExpanded]   = useState(false);
  const [sessions, setSessions]   = useState(null);  // null = not yet loaded
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const ATTENDANCE_DISPLAY = {
    present:     { label: 'Present',     className: 'bg-green-100 text-green-700',  Icon: CheckCircle2 },
    absent:      { label: 'Absent',      className: 'bg-red-100 text-red-700',      Icon: XCircle },
    rescheduled: { label: 'Rescheduled', className: 'bg-yellow-100 text-yellow-700', Icon: SkipForward },
    trial:       { label: 'Trial',       className: 'bg-blue-100 text-blue-700',    Icon: CheckCircle2 },
  };

  const handleToggle = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    // Only fetch once
    if (sessions !== null) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(
        query(
          collection(doc(db, 'studentSessions', childId), 'sessions'),
          orderBy('scheduledAt', 'desc')
        )
      );
      setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('SessionHistory fetch error:', err);
      setError('Failed to load session history.');
    } finally {
      setLoading(false);
    }
  };

  // Summary counts — only computed once sessions are loaded
  const summary = sessions ? {
    total:    sessions.length,
    present:  sessions.filter(s => s.attendance === 'present' || s.attendance === 'trial').length,
    absent:   sessions.filter(s => s.attendance === 'absent').length,
    rescheduled: sessions.filter(s => s.attendance === 'rescheduled').length,
  } : null;

  // Detect class changes for grouping label
  // Returns the classId of the previous session (by date desc) to detect switches
  function getClassSwitchLabel(sessions, index) {
    if (index === sessions.length - 1) return null; // oldest session, no previous to compare
    const current  = sessions[index];
    const previous = sessions[index + 1]; // next in desc order = older
    if (current.classId !== previous.classId) {
      return `Moved to this class on ${current.scheduledAt ? isoToDisplayDate(current.scheduledAt, getBrowserTimezone()) : '—'}`;
    }
    return null;
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      {/* Toggle header */}
      <button
        onClick={handleToggle}
        className="flex items-center justify-between w-full text-left group"
      >
        <div className="flex items-center gap-2">
          <History size={13} className="text-gray-400" />
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Session History
          </span>
          {summary && (
            <span className="text-xs text-gray-400">
              ({summary.total} sessions · {summary.present} present)
            </span>
          )}
        </div>
        {expanded
          ? <ChevronUp size={14} className="text-gray-400" />
          : <ChevronDown size={14} className="text-gray-400" />
        }
      </button>

      {expanded && (
        <div className="mt-3">
          {/* Loading */}
          {loading && (
            <div className="flex items-center gap-2 py-4 text-xs text-gray-400">
              <div className="animate-spin rounded-full h-3 w-3 border-b border-purple-600"></div>
              Loading session history…
            </div>
          )}

          {/* Error */}
          {error && (
            <p className="text-xs text-red-500 py-2">{error}</p>
          )}

          {/* Summary strip */}
          {summary && !loading && (
            <div className="flex gap-3 mb-3 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full">
                <CheckCircle2 size={11} /> {summary.present} present
              </span>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-red-50 text-red-700 rounded-full">
                <XCircle size={11} /> {summary.absent} absent
              </span>
              {summary.rescheduled > 0 && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-yellow-50 text-yellow-700 rounded-full">
                  <SkipForward size={11} /> {summary.rescheduled} rescheduled
                </span>
              )}
            </div>
          )}

          {/* Session list */}
          {sessions && !loading && sessions.length === 0 && (
            <p className="text-xs text-gray-400 italic py-2">
              No sessions recorded yet.
            </p>
          )}

          {sessions && !loading && sessions.length > 0 && (
            <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
              {sessions.map((s, idx) => {
                const cfg = ATTENDANCE_DISPLAY[s.attendance] ?? ATTENDANCE_DISPLAY.absent;
                const switchLabel = getClassSwitchLabel(sessions, idx);
                return (
                  <div key={s.id}>
                    {/* Class switch label */}
                    {switchLabel && (
                      <div className="flex items-center gap-2 my-2">
                        <div className="flex-1 h-px bg-purple-200"></div>
                        <span className="text-xs text-purple-600 font-medium whitespace-nowrap">
                          {switchLabel}
                        </span>
                        <div className="flex-1 h-px bg-purple-200"></div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-50 transition-colors">
                      {/* Date */}
                      <span className="text-xs text-gray-500 w-24 flex-shrink-0 font-mono">
                        {s.scheduledAt ? isoToDisplayDate(s.scheduledAt, getBrowserTimezone()) : '—'}
                      </span>
                      {/* Time */}
                      <span className="text-xs text-gray-400 w-16 flex-shrink-0">
                        {s.scheduledAt ? formatISOForDisplay(s.scheduledAt, getBrowserTimezone()) : ''}
                      </span>
                      {/* Teacher */}
                      <span className="text-xs text-gray-600 flex-1 truncate">
                        {s.teacherName || '—'}
                      </span>
                      {/* Attendance badge */}
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${cfg.className}`}>
                        <cfg.Icon size={10} />
                        {cfg.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL ||
  'https://us-central1-arabic-ai-kids.cloudfunctions.net';

// Date range presets
const DATE_PRESETS = [
  {
    label: 'Today',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const end = now;
      const prevStart = new Date(start.getTime() - 24 * 60 * 60 * 1000);
      const prevEnd = new Date(start.getTime() - 1);
      return { start, end, prevStart, prevEnd, comparisonLabel: 'vs. yesterday' };
    }
  },
  {
    label: 'This Week',
    getRange: () => {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const start = new Date(now);
      start.setDate(now.getDate() - dayOfWeek);
      start.setHours(0, 0, 0, 0);
      const end = now;
      const prevStart = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000);
      const prevEnd = new Date(start.getTime() - 1);
      return { start, end, prevStart, prevEnd, comparisonLabel: 'vs. last week' };
    }
  },
  {
    label: 'This Month',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = now;
      const prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevEnd = new Date(start.getTime() - 1);
      return { start, end, prevStart, prevEnd, comparisonLabel: 'vs. last month' };
    }
  },
  {
    label: 'Last 30 Days',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const end = now;
      const prevStart = new Date(start.getTime() - 30 * 24 * 60 * 60 * 1000);
      const prevEnd = new Date(start.getTime() - 1);
      return { start, end, prevStart, prevEnd, comparisonLabel: 'vs. prior 30 days' };
    }
  },
  {
    label: 'Last 90 Days',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      const end = now;
      const prevStart = new Date(start.getTime() - 90 * 24 * 60 * 60 * 1000);
      const prevEnd = new Date(start.getTime() - 1);
      return { start, end, prevStart, prevEnd, comparisonLabel: 'vs. prior 90 days' };
    }
  },
  {
    label: 'All Time',
    getRange: () => {
      return { start: null, end: null, prevStart: null, prevEnd: null, comparisonLabel: null };
    }
  }
];

// Helper functions
function isInRange(dateStr, start, end) {
  if (!dateStr || !start || !end) return true;
  try {
    const date = new Date(dateStr);
    return date >= start && date <= end;
  } catch {
    return false;
  }
}

// WARNING — not every field this function returns is date-scoped.
// The snapshot-shaped fields (totalAccounts, totalLeadAccounts, totalLeads,
// totalChildren, activeChildren, pendingChildren, and the rest of the
// account/child loop below) are NOT date-gated — that loop runs over the full
// segmentedUsers array regardless of start/end. Only newSignups,
// newSignupChildren, newChildrenAdded, newEnrollments, activeUsersInRange,
// loginUsersInRange and totalRenewals actually use the range.
//
// Consequence: previousStats.<snapshot field> is always identical to
// currentStats.<snapshot field>. Never wire a change= / computeChange() prop
// onto one of them — it would render a permanent 0% that looks like a real
// measurement. The lead figures additionally reflect CURRENT open-trial
// membership, so they are doubly not comparable across periods.
function computeStatsForRange(users, start, end, openTrialStudentIds) {
  let newSignups = 0;
  let newEnrollments = 0;
  let activeChildren = 0;
  let expiredChildren = 0;
  let pendingChildren = 0;
  let completedChildren = 0;
  let pausedChildren = 0;
  let totalChildren = 0;
  let totalLeads = 0;
  let totalAccounts = 0;
  let totalLeadAccounts = 0;
  let activeUsersInRange = 0;
  let loginUsersInRange = 0;
  let totalLifetimeRevenue = 0;
  let childrenWithMetrics = 0;
  let newSignupChildren = 0;
  let newChildrenAdded = 0;
  let totalRenewals = 0;

  const signupChildrenList = [];
  const childrenAddedList = [];
  const renewalsList = [];

  const makeRow = (user, child, dateVal) => ({
    key: `${user.uid}__${child.id}`,
    childName: child.name || '—',
    parentName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—',
    parentEmail: user.email || '—',
    date: dateVal || null,
    packageId: child.course?.packageId || null,
  });

const isAllTime = !start || !end;

  users.forEach(user => {
    const isNewAccount = isAllTime 
      ? true  // All Time: every account is a "new signup" 
      : isInRange(user.createdAt, start, end);
    
    const hasActiveChild = user.children.some(c => c.course?.status === 'active');

    if (isNewAccount && hasActiveChild) {
      newSignups++;
    }

    if (user.analytics?.lastActive && isInRange(user.analytics.lastActive, start, end)) {
      activeUsersInRange++;
    }

    // Count parents who explicitly logged in during the period
    if (user.analytics?.lastLogin && isInRange(user.analytics.lastLogin, start, end)) {
      loginUsersInRange++;
    }

    // Stage 5 audit: a trial student is a LEAD, not a customer — and a lead
    // means an OPEN trial (requested/scheduled/held/no_show), not merely
    // "courseless". Same definition as GeographicDistribution's segment and
    // AdminDashboard's fetchUsers(), via the shared openTrialStudentIds set.
    // A THREE-way split, not two:
    //   - at least one coursed child               -> totalAccounts (customer)
    //   - no coursed child, at least one OPEN lead  -> totalLeadAccounts
    //   - neither                                   -> counted in NEITHER bucket
    // The third bucket is real and deliberately uncounted here: an abandoned/
    // half-finished signup with zero children; a LEGACY account with no course
    // at all (getChildren() synthesises a one-item array — `course:
    // userProfile.course || null` — for an old childName/dateOfBirth account,
    // which is courseless but has never been within a mile of a trial); and an
    // orphaned or closed-trial courseless child (Phase 2 recon — its trial was
    // deleted "trial only", or has since gone won/lost/cancelled/expired).
    // None of those is a lead, so none of them should inflate the lead count
    // just because they aren't a customer either. This is why totalAccounts +
    // totalLeadAccounts will not sum to users.length — see the Total Accounts
    // tooltip, which explains the gap so it doesn't read as a bug.
    const hasCoursedChild = user.children.some(c => c.course);
    const hasOpenLeadChild = !hasCoursedChild && user.children.some(
      c => !c.course && c.id && openTrialStudentIds.has(c.id)
    );
    if (hasCoursedChild) {
      totalAccounts++;
    } else if (hasOpenLeadChild) {
      totalLeadAccounts++;
    }

    user.children.forEach(child => {
      if (!child.course) {
        // Only an OPEN trial lead counts as a lead — see the account-level
        // comment above for why a closed/orphaned/legacy courseless child
        // must not inflate this figure.
        if (child.id && openTrialStudentIds.has(child.id)) totalLeads++;
        return; // leads (open or not) don't participate in course-status counting below
      }
      totalChildren++;
      const status = child.course?.status;
      if (status === 'active') activeChildren++;
      else if (status === 'expired') expiredChildren++;
      else if (status === 'pending') pendingChildren++;
      else if (status === 'completed') completedChildren++;

      // "Paused" = expired AND has at least one unresolved followUps entry.
      // Paused children are a subset of expiredChildren (not a separate status).
      const hasUnresolvedFollowUp = Array.isArray(child.followUps)
        && child.followUps.some(f => !f.resolvedAt);
      if (status === 'expired' && hasUnresolvedFollowUp) {
        pausedChildren++;
      }

      if (child.course?.lastPaymentDate && isInRange(child.course.lastPaymentDate, start, end)) {
        newEnrollments++;
      }

      if (child.course?.status === 'active') {
        if (isAllTime) {
          // All Time: use date comparison to distinguish signup children vs added later
          if (child.createdAt && user.createdAt) {
            const accountDate = new Date(user.createdAt);
            const childDate = new Date(child.createdAt);
            const daysDiff = (childDate - accountDate) / (1000 * 60 * 60 * 24);
            if (daysDiff > 7) {
              newChildrenAdded++;
              childrenAddedList.push(makeRow(user, child, child.createdAt));
            } else {
              newSignupChildren++;
              signupChildrenList.push(makeRow(user, child, child.createdAt));
            }
          } else {
            // No dates available, assume signup child
            newSignupChildren++;
            signupChildrenList.push(makeRow(user, child, child.createdAt));
          }
        } else {
          // Specific date range: use account/child creation dates vs range
          if (isNewAccount && hasActiveChild) {
            newSignupChildren++;
            signupChildrenList.push(makeRow(user, child, child.createdAt));
          } else if (!isNewAccount && isInRange(child.createdAt, start, end)) {
            newChildrenAdded++;
            childrenAddedList.push(makeRow(user, child, child.createdAt));
          }
        }
      }

      if (child.metrics?.renewalCount && child.course?.lastPaymentDate &&
          isInRange(child.course.lastPaymentDate, start, end)) {
        totalRenewals++;
        renewalsList.push(makeRow(user, child, child.course.lastPaymentDate));
      }

      // Calculate lifetime revenue from metrics
      // child.metrics only exists after first renewal, so fall back to
      // course.paymentAmount (or packageId price) for first-course children
      if (child.metrics?.totalRevenue) {
        totalLifetimeRevenue += Number(child.metrics.totalRevenue);
        childrenWithMetrics++;
      } else if (child.course?.paymentAmount) {
        totalLifetimeRevenue += Number(child.course.paymentAmount);
        childrenWithMetrics++;
      } else if (child.course?.packageId && COURSE_PACKAGES[child.course.packageId]) {
        totalLifetimeRevenue += COURSE_PACKAGES[child.course.packageId].price;
        childrenWithMetrics++;
      }
    });
  });

  const avgRevenuePerChild = childrenWithMetrics > 0 
    ? Math.round(totalLifetimeRevenue / childrenWithMetrics) 
    : 0;

  return {
    newSignups,
    newSignupChildren,
    newChildrenAdded,
    newEnrollments,
    activeChildren,
    expiredChildren,
    pendingChildren,
    completedChildren,
    pausedChildren,
    totalChildren,
    totalLeads,
    activeUsersInRange,
    loginUsersInRange,
    totalAccounts,
    totalLeadAccounts,
    totalLifetimeRevenue,
    avgRevenuePerChild, 
    totalRenewals,
    signupChildrenList,
    childrenAddedList,
    renewalsList
  };
}

function computeChange(current, previous) {
  if (previous === 0 && current === 0) return { value: 0, type: 'neutral' };
  if (previous === 0) return { value: current, type: 'absolute', direction: 'up' };

  const pctChange = Math.round(((current - previous) / previous) * 100);
  const direction = pctChange > 0 ? 'up' : pctChange < 0 ? 'down' : 'neutral';
  return { value: Math.abs(pctChange), type: 'percent', direction };
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
}

function getPackageName(packageId) {
  return COURSE_PACKAGES[packageId]?.name || packageId || '—';
}

// Fetch payment history from Cloud Function
async function fetchPaymentHistory(adminUid, startDate, endDate) {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/getAdminPaymentHistory`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      adminUid,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString()
    })
  });

  if (!response.ok) {
    throw new Error(`Payment history fetch failed: ${response.status}`);
  }

  return response.json();
}

// ─── Tooltip Component ────────────────────────────────────────────────────────
// Shows an info icon that reveals a tooltip on hover.
function Tooltip({ text }) {
  return (
    <div className="relative group inline-flex">
      <Info size={13} className="cursor-help opacity-50 hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2.5 bg-gray-800 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 leading-relaxed">
        {text}
        {/* Arrow */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );
}

// Summary Card Component
function SummaryCard({ icon, label, value, color, change, comparisonLabel, tooltip, subline }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };

  return (
    <div className={`rounded-xl border p-4 ${colorMap[color] || colorMap.blue}`}>
      <div className="flex items-center gap-2 mb-2 opacity-70">
        {icon}
        <span className="text-xs font-medium flex-1">{label}</span>
        {tooltip && <Tooltip text={tooltip} />}
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {/* Sub-line for a related figure that must stay visible but not merged into
          `value` — e.g. trial-only accounts beside Total Accounts (Stage 5). */}
      {subline && <div className="text-[11px] mt-0.5 opacity-70">{subline}</div>}
      {change && comparisonLabel && change.direction !== 'neutral' && (
        <div className={`text-xs mt-1 flex items-center gap-1 ${
          change.direction === 'up' ? 'text-green-600' : 'text-red-600'
        }`}>
          {change.direction === 'up' ? '▲' : '▼'}
          {change.type === 'percent' ? `${change.value}%` : `+${change.value}`}
          <span className="opacity-70">{comparisonLabel}</span>
        </div>
      )}
    </div>
  );
}

// Sortable Table Header Component
function SortableHeader({ label, field, currentField, currentDirection, onSort }) {
  const isActive = currentField === field;
  
  return (
    <th 
      className="p-2 cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <div className="flex flex-col">
          <div className={`w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent ${
            isActive && currentDirection === 'asc' ? 'border-b-gray-700' : 'border-b-gray-300'
          }`} style={{ marginBottom: '-2px' }}></div>
          <div className={`w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent ${
            isActive && currentDirection === 'desc' ? 'border-t-gray-700' : 'border-t-gray-300'
          }`}></div>
        </div>
      </div>
    </th>
  );
}

// Monthly Revenue Chart
function MonthlyRevenueChart({ monthlyRevenue }) {
  if (!monthlyRevenue || Object.keys(monthlyRevenue).length === 0) {
    return <div className="text-gray-500 text-sm">No revenue data available</div>;
  }

  const months = Object.entries(monthlyRevenue)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6);

  const maxRevenue = Math.max(...months.map(([, data]) => data.revenue));

  return (
    <div className="space-y-2">
      {months.map(([month, data]) => {
        const barWidth = maxRevenue > 0 ? (data.revenue / maxRevenue) * 100 : 0;
        
        // FIX: Parse month string directly without Date object
        const [year, monthNum] = month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const displayMonth = `${monthNames[parseInt(monthNum) - 1]} ${year}`;

        return (
          <div key={month} className="flex items-center gap-3">
            <div className="w-20 text-xs text-gray-500 text-right shrink-0">{displayMonth}</div>
            <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-500"
                style={{ width: `${barWidth}%` }}
              />
              <span className="absolute inset-0 flex items-center px-3 text-xs font-medium">
                {data.revenue > 0 && `$${(data.revenue / 100).toFixed(0)} · ${data.count} payments`}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Timezone Calculator Modal ────────────────────────────────────────────────

const DAY_DISPLAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAY_NAME_TO_IDX   = Object.fromEntries(DAY_DISPLAY_NAMES.map((d, i) => [d.toLowerCase(), i]));

function dayDelta(fromDayName, toDayName) {
  const raw = (DAY_NAME_TO_IDX[toDayName] ?? 0) - (DAY_NAME_TO_IDX[fromDayName] ?? 0);
  if (raw > 3)  return raw - 7;  // e.g. Sun→Sat: 6 → -1
  if (raw < -3) return raw + 7;  // e.g. Sat→Sun: -6 → +1
  return raw;
}

function fmt12(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12  = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function TimezoneCalculatorModal({ onClose }) {
  const now     = new Date();
  const initDay = now.getDay(); // 0=Sun … 6=Sat
  const initTime = `${String(now.getHours()).padStart(2, '0')}:00`;
  const initTz   = getBrowserTimezone();

  const [dayIdx,   setDayIdx]   = useState(initDay);
  const [time,     setTime]     = useState(initTime);
  const [entryTz,  setEntryTz]  = useState(initTz);

  const enteredDayName = DAY_DISPLAY_NAMES[dayIdx].toLowerCase();

  // Convert entry → UTC → each target zone
  const { utcTime, utcDay } = timeToUTC(time, enteredDayName, entryTz);

  const results = CALCULATOR_ZONES.map(zone => {
    const { localTime, localDay } = timeFromUTC(utcTime, utcDay, zone.tz);
    const delta = dayDelta(enteredDayName, localDay);
    return { ...zone, localTime, localDay, delta };
  });

  // Does the entry tz match any CALCULATOR_ZONES row?
  const matchedZoneIndex = CALCULATOR_ZONES.findIndex(z => z.tz === entryTz);
  const hasMatch = matchedZoneIndex !== -1;

  // "Your input" row used when entry tz is not in CALCULATOR_ZONES
  const { localTime: inputLocalTime, localDay: inputLocalDay } =
    timeFromUTC(utcTime, utcDay, entryTz);
  const inputDelta = dayDelta(enteredDayName, inputLocalDay);

  // Build COMMON_TIMEZONES list, prepending browser tz if not present
  const tzOptions = COMMON_TIMEZONES.some(t => t.value === initTz)
    ? COMMON_TIMEZONES
    : [{ value: initTz, label: initTz }, ...COMMON_TIMEZONES];

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Globe size={18} className="text-purple-600" />
            <h2 className="text-base font-semibold text-gray-800">Timezone Calculator</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-4 space-y-3">
          {/* Inputs row */}
          <div className="flex gap-2">
            {/* Day dropdown */}
            <select
              value={dayIdx}
              onChange={e => setDayIdx(Number(e.target.value))}
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
            >
              {DAY_DISPLAY_NAMES.map((d, i) => (
                <option key={d} value={i}>{d}</option>
              ))}
            </select>

            {/* Time input */}
            <input
              type="time"
              value={time}
              onChange={e => setTime(e.target.value)}
              className="w-28 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>

          {/* Timezone dropdown */}
          <select
            value={entryTz}
            onChange={e => setEntryTz(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            {tzOptions.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          {/* Results table */}
          <div className="mt-1 rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {/* "Your input" row — only when entry tz not in CALCULATOR_ZONES */}
                {!hasMatch && (
                  <tr className="bg-purple-50 border-b border-purple-100">
                    <td className="px-3 py-2 text-base leading-none">📍</td>
                    <td className="px-2 py-2 text-xs font-medium text-purple-700 truncate max-w-0 w-full">
                      Your input
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-500 whitespace-nowrap capitalize">
                      {inputLocalDay}
                    </td>
                    <td className="px-3 py-2 text-xs font-semibold text-gray-800 whitespace-nowrap">
                      {fmt12(inputLocalTime)}
                      {inputDelta !== 0 && (
                        <span className="ml-1 px-1 py-0.5 text-[10px] font-bold bg-yellow-100 text-yellow-700 rounded">
                          {inputDelta > 0 ? `+${inputDelta}d` : `${inputDelta}d`}
                        </span>
                      )}
                    </td>
                  </tr>
                )}

                {results.map((zone, i) => {
                  const isHighlighted = hasMatch && i === matchedZoneIndex;
                  return (
                    <tr
                      key={zone.tz}
                      className={`border-b border-gray-50 last:border-0 ${
                        isHighlighted ? 'bg-purple-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                      }`}
                    >
                      <td className="px-3 py-2 text-base leading-none">{zone.flag}</td>
                      <td className="px-2 py-2 text-xs text-gray-600 truncate max-w-0 w-full">
                        {zone.label}
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-400 whitespace-nowrap capitalize">
                        {zone.localDay}
                      </td>
                      <td className="px-3 py-2 text-xs font-semibold text-gray-800 whitespace-nowrap">
                        {fmt12(zone.localTime)}
                        {zone.delta !== 0 && (
                          <span className="ml-1 px-1 py-0.5 text-[10px] font-bold bg-yellow-100 text-yellow-700 rounded">
                            {zone.delta > 0 ? `+${zone.delta}d` : `${zone.delta}d`}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Status display registry ────────────────────────────────────────────────
// Maps known course statuses to pill styling and display priority.
// Unknown statuses fall back to UNKNOWN_STATUS_PILL_CLASS so they remain
// visible in the UI (rather than being silently hidden) when new statuses
// are introduced. To add styling for a new status, add an entry here.
//
// Priority controls display order in the row: lower = more urgent / shown first.
// `completed` and `expired` share the red palette because both indicate
// "renewal needed" from the admin's perspective.
const STATUS_CONFIG = {
  expired:     { pillClass: 'bg-red-100 text-red-700',       priority: 1 },
  completed:   { pillClass: 'bg-red-100 text-red-700',       priority: 2 },
  active:      { pillClass: 'bg-green-100 text-green-700',   priority: 3 },
  paused:      { pillClass: 'bg-amber-100 text-amber-700',   priority: 4 },
  pending:     { pillClass: 'bg-yellow-100 text-yellow-700', priority: 5 },
  deactivated: { pillClass: 'bg-gray-200 text-gray-600',     priority: 6 },
};
const UNKNOWN_STATUS_PRIORITY = 999;
const UNKNOWN_STATUS_PILL_CLASS = 'bg-slate-100 text-slate-600';

// Hex colors for status segments in charts (Recharts needs hex, not Tailwind).
// Any status missing here falls back to UNKNOWN_STATUS_CHART_COLOR, so new
// statuses still render — they just get a neutral color until added here.
const STATUS_CHART_COLORS = {
  active:      '#10b981',
  expired:     '#ef4444',
  completed:   '#f87171',
  paused:      '#f59e0b',
  pending:     '#eab308',
  deactivated: '#9ca3af',
  none:        '#cbd5e1',
};
const UNKNOWN_STATUS_CHART_COLOR = '#cbd5e1';
const statusColor = (s) => STATUS_CHART_COLORS[s] ?? UNKNOWN_STATUS_CHART_COLOR;

// Stage 5 audit — trial statuses that still represent a live pipeline lead.
// won/lost/cancelled/expired are excluded on purpose: see the fetch in
// fetchUsers() and the comment on GeographicDistribution's LEAD_SEGMENT_KEY.
const OPEN_TRIAL_STATUSES = new Set(['requested', 'scheduled', 'held', 'no_show']);

// Capitalize first letter for display in dropdowns / labels
const formatStatusLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Main Dashboard Component
function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if current user is super admin
  const userIsSuperAdmin = isSuperAdmin(user?.uid);
  const userIsAdmin = isAdmin(user?.uid);

  const [showCalc, setShowCalc] = useState(false);

  // Data state
  const [users, setUsers] = useState([]);
  const [paymentData, setPaymentData] = useState(null);

  // Loading & error state
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentError, setPaymentError] = useState(null);

  // Filter state
  const [selectedDatePreset, setSelectedDatePreset] = useState('All Time');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [audienceSegment, setAudienceSegment] = useState('all'); // 'all' | 'kids' | 'adults'

  const [classes, setClasses] = useState([]);
  // Stage 5 audit: which OPEN trials exist, keyed by studentId, so
  // GeographicDistribution can show a courseless lead as a lead — never as an
  // unlabelled 'none' status, and never a closed/expired one forever (see the
  // fetch below for the status filter).
  const [openTrialStudentIds, setOpenTrialStudentIds] = useState(new Set());
  const [syncingEnrollments, setSyncingEnrollments] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedChildForUpload, setSelectedChildForUpload] = useState(null);
  const [selectedUserForUpload, setSelectedUserForUpload] = useState(null);
  const [deletingReportCard, setDeletingReportCard] = useState(null);

  // ── Class link state ─────────────────────────────────────────────────────────
  // Tracks the current input value per child (keyed by childId) and saving state.
  // TEMPORARY FEATURE — remove this block (and matching UI) when the calendar
  // booking system is live and Zoom links are no longer needed.
  const [classLinkInputs, setClassLinkInputs] = useState({});   // { [childId]: string }
  const [savingClassLink, setSavingClassLink]  = useState(null); // childId being saved
  const [classLinkErrors, setClassLinkErrors] = useState({});   // { [childId]: string }

  // Growth Analytics chart state
  const [chartGranularity, setChartGranularity] = useState('monthly');
  const [activeGrowthTab, setActiveGrowthTab] = useState('signups');
  const [activePeopleTab, setActivePeopleTab] = useState('signups');
  const [passwordResetStatus, setPasswordResetStatus] = useState({}); // keyed by user.uid
  const [tempPasswordState, setTempPasswordState] = useState({});     // keyed by user.uid
  const [impersonateStatus, setImpersonateStatus] = useState({});     // keyed by user.uid

  // Fetch users from Firestore
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersSnapshot, classesSnapshot, trialsSnapshot] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'classes')),
        getDocs(collection(db, 'trials')),
      ]);
      const usersData = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          ...data,
          children: getChildren(data)
        };
      });

      setUsers(usersData);

      // Store classes for enrollment lookup
      setClasses(classesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

      // Stage 5: OPEN trials only (requested/scheduled/held/no_show). won/lost/
      // cancelled/expired are deliberately excluded — a courseless child whose
      // trial has closed is not a live lead, and including it would make the
      // lead count (here and in GeographicDistribution) accumulate dead leads
      // forever. This is the SINGLE definition of "lead" used everywhere on
      // this page — Total Students' sub-line, Total Accounts' split, the
      // Children-column suffix, and the map's segment all read this same set.
      //
      // This is a CURRENT-STATE fact, not a period one: "is this trial open
      // right now" has no relationship to selectedDatePreset/customStart/
      // customEnd. Every figure built from this set stays fixed while the
      // admin changes the date range, on purpose — labelled "(current)" where
      // it's shown, so that not moving reads as correct, not broken.
      const openStudentIds = new Set();
      trialsSnapshot.docs.forEach(d => {
        const t = d.data();
        if (t.studentId && OPEN_TRIAL_STATUSES.has(t.status)) {
          openStudentIds.add(t.studentId);
        }
      });
      setOpenTrialStudentIds(openStudentIds);

    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch payments when date range changes (only for super admins).
  // Defined inside useEffect with an AbortController so that if the user
  // switches date presets before a slow request resolves, the stale response
  // is cancelled and never overwrites state with wrong data (race condition fix).
  useEffect(() => {
    if (!user?.uid || !userIsSuperAdmin) return;

    const controller = new AbortController();

    const fetchPayments = async () => {
      try {
        setPaymentLoading(true);
        setPaymentError(null);

        let fetchStart, end;
        if (selectedDatePreset === 'Custom') {
          fetchStart = customStart ? new Date(customStart + 'T00:00:00') : null;
          end = customEnd ? new Date(customEnd + 'T23:59:59') : null;
        } else {
          const preset = DATE_PRESETS.find(p => p.label === selectedDatePreset);
          const range = preset.getRange();
          // Use prevStart so the server returns BOTH the current and previous periods.
          // revenueStats splits them client-side. Without this, selecting 'Today'
          // only fetches today's payments so previousRevenue always reads /bin/sh.
          fetchStart = range.prevStart ?? range.start;
          end = range.end;
        }

        const response = await fetch(`${FUNCTIONS_BASE_URL}/getAdminPaymentHistory`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            adminUid: user.uid,
            startDate: fetchStart?.toISOString(),
            endDate: end?.toISOString()
          }),
          signal: controller.signal
        });

        if (!response.ok) throw new Error(`Payment history fetch failed: ${response.status}`);

        const data = await response.json();

        if (!controller.signal.aborted) {
          setPaymentData(data);
        }
      } catch (err) {
        if (err.name === 'AbortError') return; // Stale request cancelled — ignore silently
        console.error('Error fetching payment history:', err);
        setPaymentError('Failed to load payment history');
      } finally {
        if (!controller.signal.aborted) {
          setPaymentLoading(false);
        }
      }
    };

    fetchPayments();

    // Cancel any in-flight request when date range changes or component unmounts
    return () => controller.abort();
  }, [selectedDatePreset, customStart, customEnd, user, userIsSuperAdmin]);

  // Single upstream filter: every analytics consumer reads segmentedUsers, not users.
  // 'all' → everyone; 'adults' → accountType === 'adult'; 'kids' → everything else.
  const segmentedUsers = useMemo(() => {
    if (audienceSegment === 'all') return users;
    if (audienceSegment === 'adults') return users.filter(u => u.accountType === 'adult');
    return users.filter(u => u.accountType !== 'adult');
  }, [users, audienceSegment]);

  // Compute stats for current and previous periods
  const { currentStats, previousStats, comparisonLabel } = useMemo(() => {
    if (selectedDatePreset === 'Custom') {
      const start = customStart ? new Date(customStart + 'T00:00:00') : null;
      const end = customEnd ? new Date(customEnd + 'T23:59:59') : null;
      const currentStats = computeStatsForRange(segmentedUsers, start, end, openTrialStudentIds);
      return { currentStats, previousStats: null, comparisonLabel: null };
    }

    const preset = DATE_PRESETS.find(p => p.label === selectedDatePreset);
    const range = preset.getRange();

    const currentStats = computeStatsForRange(segmentedUsers, range.start, range.end, openTrialStudentIds);
    const previousStats = range.prevStart && range.prevEnd
      ? computeStatsForRange(segmentedUsers, range.prevStart, range.prevEnd, openTrialStudentIds)
      : null;

    return {
      currentStats,
      previousStats,
      comparisonLabel: range.comparisonLabel
    };
  }, [segmentedUsers, selectedDatePreset, customStart, customEnd, openTrialStudentIds]);

  // ── Deactivated / Paused counts for the selected period (from statusHistory) ──
  const lifecyclePeriodCounts = useMemo(() => {
    let rangeStart = null;
    let rangeEnd = null;

    if (selectedDatePreset === 'Custom') {
      rangeStart = customStart ? new Date(customStart + 'T00:00:00') : null;
      rangeEnd = customEnd ? new Date(customEnd + 'T23:59:59') : null;
    } else if (selectedDatePreset !== 'All Time') {
      const preset = DATE_PRESETS.find(p => p.label === selectedDatePreset);
      const range = preset.getRange();
      rangeStart = range.start;
      rangeEnd = range.end;
    }

    let deactivated = 0;
    let paused = 0;
    (segmentedUsers || []).forEach(u => {
      (u.children || []).forEach(child => {
        const history = Array.isArray(child.statusHistory) ? child.statusHistory : [];
        history.forEach(entry => {
          if (!entry || !entry.changedAt) return;
          const d = new Date(entry.changedAt);
          if (isNaN(d.getTime())) return;
          if (rangeStart && d < rangeStart) return;
          if (rangeEnd && d > rangeEnd) return;
          if (entry.source === 'deactivate') deactivated++;
          else if (entry.source === 'pause') paused++;
        });
      });
    });
    return { deactivated, paused };
  }, [segmentedUsers, selectedDatePreset, customStart, customEnd]);

  // ── Enrollment split: of active children, how many are in a class ───────────
  // Snapshot, not period-filtered.
  const enrollmentSplit = useMemo(() => {
    // Set of "userId__childId" for every student in a non-suspended class
    const enrolledKeys = new Set();
    (classes || []).forEach(cls => {
      if (cls.status === 'suspended') return;
      (cls.enrolledStudents ?? []).forEach(s => {
        enrolledKeys.add(`${s.userId}__${s.childId}`);
      });
    });

    let activeEnrolled = 0;
    let activeNotEnrolled = 0;
    (segmentedUsers || []).forEach(u => {
      (u.children || []).forEach(child => {
        if (child.course?.status !== 'active') return;
        const key = `${u.uid}__${child.id}`;
        if (enrolledKeys.has(key)) activeEnrolled++;
        else activeNotEnrolled++;
      });
    });

    return { activeEnrolled, activeNotEnrolled };
  }, [segmentedUsers, classes]);

  // Unresolved follow-ups count (across all users)
  const unresolvedFollowUpCount = useMemo(() => {
    if (!Array.isArray(users)) return 0;
    let n = 0;
    users.forEach(u => {
      (u.children || []).forEach(c => {
        (c.followUps || []).forEach(f => {
          if (!f.resolvedAt) n++;
        });
      });
    });
    return n;
  }, [users]);

  // Revenue stats from payment data
  const revenueStats = useMemo(() => {
    if (!paymentData?.payments) return null;

    let range;
    if (selectedDatePreset === 'Custom') {
      const start = customStart ? new Date(customStart + 'T00:00:00') : null;
      const end = customEnd ? new Date(customEnd + 'T23:59:59') : null;
      range = { start, end, prevStart: null, prevEnd: null };
    } else {
      const preset = DATE_PRESETS.find(p => p.label === selectedDatePreset);
      range = preset.getRange();
    }

    const filterPayments = (payments, start, end) => {
      if (!start || !end) return payments;
      return payments.filter(p => {
        const date = new Date(p.created);
        return date >= start && date <= end;
      });
    };

    const currentPayments = filterPayments(paymentData.payments, range.start, range.end);
    const previousPayments = range.prevStart && range.prevEnd
      ? filterPayments(paymentData.payments, range.prevStart, range.prevEnd)
      : null;

    const currentRevenue = currentPayments.reduce((sum, p) => sum + p.amount, 0);
    const previousRevenue = previousPayments
      ? previousPayments.reduce((sum, p) => sum + p.amount, 0)
      : null;

    const currentRenewals = currentPayments.filter(p => p.isRenewal).length;
    const previousRenewals = previousPayments
      ? previousPayments.filter(p => p.isRenewal).length
      : null;

    return {
      revenue: currentRevenue,
      previousRevenue,
      paymentCount: currentPayments.length,
      previousPaymentCount: previousPayments?.length || null,
      renewals: currentRenewals,
      previousRenewals,
      discounted: currentPayments.filter(p => p.discountCode).length
    };
  }, [paymentData, selectedDatePreset, customStart, customEnd]);

  // Compute monthly revenue using LOCAL timezone (not UTC)
  const localMonthlyRevenue = useMemo(() => {
    if (!paymentData?.payments) return {};
    
    const monthlyData = {};
    
    paymentData.payments.forEach(p => {
      const date = new Date(p.created);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const monthKey = `${year}-${month}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { revenue: 0, count: 0, renewals: 0, newSignups: 0 };
      }
      
      monthlyData[monthKey].revenue += p.amount;
      monthlyData[monthKey].count++;
      
      if (p.isRenewal) {
        monthlyData[monthKey].renewals++;
      } else {
        monthlyData[monthKey].newSignups++;
      }
    });
    
    return monthlyData;
  }, [paymentData]);

  // Auto-reset chartGranularity when the date range changes
  useEffect(() => {
    const getSpanDays = () => {
      if (selectedDatePreset === 'All Time') return Infinity;
      if (selectedDatePreset === 'Custom') {
        if (!customStart || !customEnd) return 30;
        const s = new Date(customStart + 'T00:00:00');
        const e = new Date(customEnd + 'T23:59:59');
        return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)));
      }
      const preset = DATE_PRESETS.find(p => p.label === selectedDatePreset);
      const range = preset.getRange();
      if (!range.start || !range.end) return Infinity;
      return Math.max(1, Math.round((range.end - range.start) / (1000 * 60 * 60 * 24)));
    };
    const span = getSpanDays();
    if (span === Infinity) {
      setChartGranularity('monthly');
    } else if (span < 14) {
      setChartGranularity('daily');
    } else if (span < 60) {
      setChartGranularity('weekly');
    } else {
      setChartGranularity('monthly');
    }
  }, [selectedDatePreset, customStart, customEnd]);

  // Growth chart data
  const growthChartData = useMemo(() => {
    // Determine date range
    let rangeStart = null;
    let rangeEnd = null;
    let isAllTime = false;

    if (selectedDatePreset === 'All Time') {
      isAllTime = true;
    } else if (selectedDatePreset === 'Custom') {
      rangeStart = customStart ? new Date(customStart + 'T00:00:00') : null;
      rangeEnd = customEnd ? new Date(customEnd + 'T23:59:59') : null;
    } else {
      const preset = DATE_PRESETS.find(p => p.label === selectedDatePreset);
      const range = preset.getRange();
      rangeStart = range.start;
      rangeEnd = range.end;
      if (!rangeStart || !rangeEnd) isAllTime = true;
    }

    const inRange = (dateStr) => {
      if (!dateStr) return false;
      if (isAllTime) return true;
      if (!rangeStart || !rangeEnd) return true;
      try {
        const d = new Date(dateStr);
        return d >= rangeStart && d <= rangeEnd;
      } catch { return false; }
    };

    // Bucket key helpers
    const getMonday = (d) => {
      const day = d.getDay();
      const diff = (day === 0 ? -6 : 1 - day);
      const mon = new Date(d);
      mon.setDate(d.getDate() + diff);
      return mon;
    };

    const bucketKey = (dateStr) => {
      try {
        const d = new Date(dateStr);
        if (chartGranularity === 'daily') {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        } else if (chartGranularity === 'weekly') {
          const mon = getMonday(d);
          return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, '0')}-${String(mon.getDate()).padStart(2, '0')}`;
        } else if (chartGranularity === 'monthly') {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        } else {
          return `${d.getFullYear()}`;
        }
      } catch { return null; }
    };

    const bucketLabel = (key) => {
      try {
        if (chartGranularity === 'daily' || chartGranularity === 'weekly') {
          const [y, m, day] = key.split('-');
          const d = new Date(Number(y), Number(m) - 1, Number(day));
          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else if (chartGranularity === 'monthly') {
          const [y, m] = key.split('-');
          const d = new Date(Number(y), Number(m) - 1, 1);
          return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        } else {
          return key;
        }
      } catch { return key; }
    };

    // Collect all events into buckets
    const signupAccountBuckets = {};  // key -> count of new accounts
    const signupChildBuckets = {};    // key -> count of new signup children
    const renewalBuckets = {};        // key -> count of renewals
    const childrenAddedBuckets = {};  // key -> count of children added to existing accounts

    segmentedUsers.forEach(user => {
      if (!user.createdAt) return;

      const hasEnrolledChild = user.children.some(
        c => c.course?.status === 'active' || c.course?.status === 'expired'
      );
      if (!hasEnrolledChild) return;

      // New Signups (accounts)
      if (inRange(user.createdAt)) {
        const k = bucketKey(user.createdAt);
        if (k) signupAccountBuckets[k] = (signupAccountBuckets[k] || 0) + 1;
      }

      const accountDate = user.createdAt ? new Date(user.createdAt) : null;

      user.children.forEach(child => {
        if (!child.course) return; // skip unenrolled children

        const childDate = child.createdAt ? new Date(child.createdAt) : null;
        const daysDiff = (accountDate && childDate)
          ? (childDate - accountDate) / (1000 * 60 * 60 * 24)
          : 0;

        // New signup children: created within 7 days of account
        if (child.createdAt && inRange(child.createdAt) && daysDiff <= 7) {
          const k = bucketKey(user.createdAt); // bucket by account creation date
          if (k) signupChildBuckets[k] = (signupChildBuckets[k] || 0) + 1;
        }

        // Children added to existing accounts: created > 7 days after account
        if (child.createdAt && inRange(child.createdAt) && daysDiff > 7) {
          const k = bucketKey(child.createdAt);
          if (k) childrenAddedBuckets[k] = (childrenAddedBuckets[k] || 0) + 1;
        }

        // Renewals: each courseHistory entry with an archivedDate = 1 renewal
        if (Array.isArray(child.courseHistory)) {
          child.courseHistory.forEach(entry => {
            if (!entry.archivedDate) return;
            if (!inRange(entry.archivedDate)) return;
            const k = bucketKey(entry.archivedDate);
            if (k) renewalBuckets[k] = (renewalBuckets[k] || 0) + 1;
          });
        }
      });
    });

    // ── Lifecycle event buckets (from statusHistory) ──────────────────────────
    const lifecycleBuckets = {};
    const ensureLifecycleBucket = (k) => {
      if (!lifecycleBuckets[k]) {
        lifecycleBuckets[k] = { new_signup: 0, renewal: 0, completion_job: 0, deactivate: 0, pause: 0 };
      }
      return lifecycleBuckets[k];
    };

    segmentedUsers.forEach(u => {
      (u.children || []).forEach(child => {
        const history = Array.isArray(child.statusHistory) ? child.statusHistory : [];
        history.forEach(entry => {
          if (!entry || !entry.changedAt || !entry.source) return;
          if (!['new_signup', 'renewal', 'completion_job', 'deactivate', 'pause'].includes(entry.source)) return;
          if (!inRange(entry.changedAt)) return;
          const k = bucketKey(entry.changedAt);
          if (k) ensureLifecycleBucket(k)[entry.source]++;
        });
      });
    });

    // Build a sorted list of all unique bucket keys across all data sets
    const allKeys = new Set([
      ...Object.keys(signupAccountBuckets),
      ...Object.keys(signupChildBuckets),
      ...Object.keys(renewalBuckets),
      ...Object.keys(childrenAddedBuckets),
      ...Object.keys(lifecycleBuckets),
    ]);

    if (allKeys.size === 0) {
      return { signups: [], renewals: [], childrenAdded: [], totals: { signups: 0, renewals: 0, childrenAdded: 0 }, lifecycle: [], lifecycleTotals: { new_signup: 0, renewal: 0, completion_job: 0, deactivate: 0, pause: 0 } };
    }

    // Fill in zero-count buckets for the full span between min and max key
    const sortedKeys = Array.from(allKeys).sort();
    const firstKey = sortedKeys[0];
    const lastKey = sortedKeys[sortedKeys.length - 1];

    const filledKeys = [];
    if (chartGranularity === 'daily') {
      const cur = new Date(firstKey + 'T00:00:00');
      const end = new Date(lastKey + 'T00:00:00');
      while (cur <= end) {
        filledKeys.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`);
        cur.setDate(cur.getDate() + 1);
      }
    } else if (chartGranularity === 'weekly') {
      const cur = new Date(firstKey + 'T00:00:00');
      const end = new Date(lastKey + 'T00:00:00');
      while (cur <= end) {
        filledKeys.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`);
        cur.setDate(cur.getDate() + 7);
      }
    } else if (chartGranularity === 'monthly') {
      const [fy, fm] = firstKey.split('-').map(Number);
      const [ly, lm] = lastKey.split('-').map(Number);
      let y = fy, m = fm;
      while (y < ly || (y === ly && m <= lm)) {
        filledKeys.push(`${y}-${String(m).padStart(2, '0')}`);
        m++;
        if (m > 12) { m = 1; y++; }
      }
    } else {
      const fy = Number(firstKey);
      const ly = Number(lastKey);
      for (let y = fy; y <= ly; y++) filledKeys.push(`${y}`);
    }

    const signups = filledKeys.map(k => ({
      label: bucketLabel(k),
      newAccounts: signupAccountBuckets[k] || 0,
      newChildren: signupChildBuckets[k] || 0,
    }));

    const renewals = filledKeys.map(k => ({
      label: bucketLabel(k),
      renewals: renewalBuckets[k] || 0,
    }));

    const childrenAdded = filledKeys.map(k => ({
      label: bucketLabel(k),
      childrenAdded: childrenAddedBuckets[k] || 0,
    }));

    const totals = {
      signups: signups.reduce((s, b) => s + b.newAccounts, 0),
      renewals: renewals.reduce((s, b) => s + b.renewals, 0),
      childrenAdded: childrenAdded.reduce((s, b) => s + b.childrenAdded, 0),
    };

    const lifecycle = filledKeys.map(k => ({
      label: bucketLabel(k),
      new_signup:     (lifecycleBuckets[k]?.new_signup)     || 0,
      renewal:        (lifecycleBuckets[k]?.renewal)        || 0,
      completion_job: (lifecycleBuckets[k]?.completion_job) || 0,
      deactivate:     (lifecycleBuckets[k]?.deactivate)     || 0,
      pause:          (lifecycleBuckets[k]?.pause)          || 0,
    }));

    const lifecycleTotals = {
      new_signup:     lifecycle.reduce((s, b) => s + b.new_signup, 0),
      renewal:        lifecycle.reduce((s, b) => s + b.renewal, 0),
      completion_job: lifecycle.reduce((s, b) => s + b.completion_job, 0),
      deactivate:     lifecycle.reduce((s, b) => s + b.deactivate, 0),
      pause:          lifecycle.reduce((s, b) => s + b.pause, 0),
    };

    return { signups, renewals, childrenAdded, totals, lifecycle, lifecycleTotals };
  }, [segmentedUsers, selectedDatePreset, customStart, customEnd, chartGranularity]);

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Auto-discover all unique child course statuses present in the user data.
  // Used to populate the status filter dropdown dynamically so new statuses
  // appear without code changes. Ordered by STATUS_CONFIG priority (known
  // statuses first in urgency order), then alphabetically for unknowns.
  const availableStatuses = useMemo(() => {
    const set = new Set();
    users.forEach(u => {
      (u.children ?? []).forEach(c => {
        if (c.course?.status) set.add(c.course.status);
      });
    });
    return Array.from(set).sort((a, b) => {
      const pa = STATUS_CONFIG[a]?.priority ?? UNKNOWN_STATUS_PRIORITY;
      const pb = STATUS_CONFIG[b]?.priority ?? UNKNOWN_STATUS_PRIORITY;
      if (pa !== pb) return pa - pb;
      return a.localeCompare(b);
    });
  }, [users]);

  // Filter and sort users — derives from segmentedUsers so search/status stack on top of segment
  const filteredUsers = useMemo(() => {
    let filtered = [...segmentedUsers];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const digitsTerm = term.replace(/\D/g, '');
      filtered = filtered.filter(u => {
        const nameMatch = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(term);
        const emailMatch = u.email?.toLowerCase().includes(term);
        const childMatch = u.children.some(c => c.name?.toLowerCase().includes(term));
        const phoneMatch = digitsTerm.length > 0
          && (u.phoneNumber || '').replace(/\D/g, '').includes(digitsTerm);
        const locationMatch = [u.city, u.stateProvince, u.country]
          .some(v => v?.toLowerCase().includes(term));
        return nameMatch || emailMatch || childMatch || phoneMatch || locationMatch;
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(u => 
        u.children.some(c => c.course?.status === statusFilter)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      if (sortField === 'createdAt') {
        aVal = new Date(a.createdAt || 0);
        bVal = new Date(b.createdAt || 0);
      } else if (sortField === 'name') {
        aVal = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
        bVal = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
      } else if (sortField === 'email') {
        aVal = a.email || '';
        bVal = b.email || '';
      } else if (sortField === 'lastActive') {
        aVal = new Date(a.analytics?.lastActive || 0);
        bVal = new Date(b.analytics?.lastActive || 0);
      } else if (sortField === 'children') {
        // Coursed children only — a family with 3 trial leads and 0 paying
        // children must not out-sort one with 1 real student (Stage 5 audit).
        aVal = a.children.filter(c => c.course).length;
        bVal = b.children.filter(c => c.course).length;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [segmentedUsers, searchTerm, statusFilter, sortField, sortDirection]);

  // Engagement stats
  const engagementStats = useMemo(() => {
    let totalPlayTime = 0;
    let totalSessions = 0;
    let totalScore = 0;
    let totalAttempts = 0;
    let totalCorrect = 0;

    segmentedUsers.forEach(u => {
      if (u.analytics) {
        totalPlayTime += u.analytics.totalPlayTime || 0;
        totalSessions += u.analytics.sessionsCount || 0;
        totalScore += u.analytics.totalScore || 0;
        totalAttempts += u.analytics.totalAttempts || 0;
        totalCorrect += u.analytics.totalCorrect || 0;
      }
    });

    const avgAccuracy = totalAttempts > 0 ? ((totalCorrect / totalAttempts) * 100).toFixed(1) : 0;

    return {
      totalPlayTime,
      totalSessions,
      avgPlayTimePerSession: totalSessions > 0 ? Math.round(totalPlayTime / totalSessions) : 0,
      avgAccuracy
    };
  }, [segmentedUsers]);

  // Super-admin only: get an impersonation link and copy it to the clipboard.
  const handleImpersonate = async (targetUid) => {
    try {
      setImpersonateStatus((p) => ({ ...p, [targetUid]: 'Generating…' }));
      const idToken = await user.getIdToken();
      const resp = await fetch(`${FUNCTIONS_BASE_URL}/impersonateUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ targetUid }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.customToken) {
        setImpersonateStatus((p) => ({ ...p, [targetUid]: data.error || 'Failed' }));
        return;
      }
      const link = `${window.location.origin}/impersonate?token=${encodeURIComponent(data.customToken)}`;
      await navigator.clipboard.writeText(link);
      setImpersonateStatus((p) => ({ ...p, [targetUid]: 'Link copied — paste in incognito' }));
      setTimeout(() => setImpersonateStatus((p) => ({ ...p, [targetUid]: null })), 6000);
    } catch (err) {
      console.error('Impersonation link error:', err);
      setImpersonateStatus((p) => ({ ...p, [targetUid]: 'Error' }));
    }
  };

  // Sync all denormalized enrollment data against live users collection
  // Super admin only
  const handleSyncEnrollmentData = async () => {
    if (!window.confirm(
      'This will reconcile all enrollment data in classes against the current users collection. Continue?'
    )) return;

    setSyncingEnrollments(true);
    setSyncResult(null);

    try {
      const response = await fetch(`${FUNCTIONS_BASE_URL}/syncEnrollmentData`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestedBy: user.uid })
      });

      if (!response.ok) throw new Error('Sync failed');

      const result = await response.json();
      setSyncResult({
        success: true,
        message: `Sync complete — ${result.updated ?? 0} enrollment records updated.`
      });
    } catch (err) {
      console.error('Sync error:', err);
      setSyncResult({
        success: false,
        message: 'Sync failed. Check the console for details.'
      });
    } finally {
      setSyncingEnrollments(false);
    }
  };

  // Handle opening upload modal
const handleOpenUploadModal = (user, child) => {
  setSelectedUserForUpload(user);
  setSelectedChildForUpload(child);
  setUploadModalOpen(true);
};

// Handle successful upload
const handleUploadSuccess = (newReportCard) => {
  fetchUsers(); // Refresh data
};

// Handle delete report card
// ── TEMPORARY: Class link save ────────────────────────────────────────────────
// Writes classLink directly to the child object inside users/{userId}.
// No Cloud Function needed — it's just a string field.
// Remove this function (and matching UI) when calendar booking is live.
const handleSaveClassLink = async (userId, childId) => {
  const raw = classLinkInputs[childId] ?? '';
  const { value, meetingId, passcode, error } = extractMeetingLink(raw);

  if (error) {
    setClassLinkErrors(prev => ({ ...prev, [childId]: error }));
    return;
  }
  setClassLinkErrors(prev => {
    const next = { ...prev };
    delete next[childId];
    return next;
  });

  setSavingClassLink(childId);
  try {
    // Read current user doc children, update the matching child, write back
    const userRef = doc(db, 'users', userId);
    const userDoc = users.find(u => u.uid === userId);
    if (!userDoc) throw new Error('User not found');
    const updatedChildren = (userDoc.children ?? []).map(c =>
      c.id === childId
        ? { ...c, classLink: value, classLinkMeetingId: meetingId, classLinkPasscode: passcode }
        : c
    );
    await updateDoc(userRef, { children: updatedChildren });
    setClassLinkInputs(prev => ({ ...prev, [childId]: value || '' }));
    await fetchUsers();
  } catch (err) {
    console.error('Save class link error:', err);
    alert('Failed to save class link. Please try again.');
  } finally {
    setSavingClassLink(null);
  }
};

// Handle delete report card
const handleDeleteReportCard = async (userId, childId, reportCardId) => {
  if (!window.confirm('Are you sure you want to delete this report card?')) {
    return;
  }

  setDeletingReportCard(reportCardId); // ← NEW: Set loading state

  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/deleteReportCard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, childId, reportCardId })
    });

    if (!response.ok) {
      throw new Error('Failed to delete report card');
    }

    await fetchUsers(); // ← NEW: Added 'await' here
  } catch (error) {
    console.error('Delete error:', error);
    alert('Failed to delete report card');
  } finally {
    setDeletingReportCard(null); // ← NEW: Clear loading state
  }
};

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchUsers}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showCalc && <TimezoneCalculatorModal onClose={() => setShowCalc(false)} />}
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
              <button
                onClick={() => setShowCalc(true)}
                title="Timezone Calculator"
                className="p-2 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors"
              >
                <Globe size={18} />
              </button>
            </div>
            <p className="text-gray-600">Platform analytics and user management</p>
          </div>

          {/* Date Range Selector */}
          <div className="flex flex-wrap gap-2 items-center">
            {DATE_PRESETS.map(preset => (
              <button
                key={preset.label}
                onClick={() => setSelectedDatePreset(preset.label)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedDatePreset === preset.label
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {preset.label}
              </button>
            ))}
            <button
              onClick={() => setSelectedDatePreset('Custom')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedDatePreset === 'Custom'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              Custom
            </button>
            {selectedDatePreset === 'Custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                  className="px-3 py-2 rounded-lg text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <span className="text-gray-500 text-sm">to</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                  className="px-3 py-2 rounded-lg text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Attendance Module Navigation */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Scheduling & Attendance
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">Manage classes, teachers, and daily attendance</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/admin/attendance')}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Activity size={16} />
                Attendance
              </button>
              <button
                onClick={() => navigate('/admin/classes')}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <Calendar size={16} />
                Classes
              </button>
              <button onClick={() => navigate('/admin/renewals')} className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                <Clock size={16} />
                Renewals
              </button>
              <button
                onClick={() => navigate('/admin/trials')}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <UserPlus size={16} />
                Trials
              </button>
              <button
                onClick={() => navigate('/admin/referral-codes')}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <Link size={16} />
                Referral Codes
              </button>
              <button
                onClick={() => navigate('/admin/followups')}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors relative"
              >
                <Pause size={16} />
                Follow-Ups
                {unresolvedFollowUpCount > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-amber-500 rounded-full">
                    {unresolvedFollowUpCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate('/admin/teachers')}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <Users size={16} />
                Teachers
              </button>
              <button
                onClick={() => navigate('/admin/teacher-reports')}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <FileText size={16} />
                Teacher Reports
              </button>
              <button
                onClick={() => navigate('/admin/email-triggers')}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <Mail size={16} />
                Email Triggers
              </button>
              {userIsSuperAdmin && (
                <button
                  onClick={handleSyncEnrollmentData}
                  disabled={syncingEnrollments}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={16} className={syncingEnrollments ? 'animate-spin' : ''} />
                  {syncingEnrollments ? 'Syncing...' : 'Sync Enrollment Data'}
                </button>
              )}
            </div>
          </div>
          {syncResult && (
            <div className={`mt-3 text-xs px-3 py-2 rounded-lg ${
              syncResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {syncResult.message}
            </div>
          )}
        </div>

        {/* Content Management Navigation */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Content Management
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">Manage downloadable resources and educational materials</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/admin/resources')}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <BookOpen size={16} />
                Student Resources
              </button>
              <button
                onClick={() => navigate('/admin/teacher-resources')}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <BookOpen size={16} />
                Teacher Resources
              </button>
            </div>
          </div>
        </div>

        {/* Audience Segment Toggle — a FILTER CONTROL, not a metric. Its counts
            are deliberately every registered account (including trial-only
            ones): they describe what clicking the button shows below, and must
            keep matching the rows actually filtered in, or the control lies
            about its own effect. This is why it can show a bigger number than
            the "Total Accounts" card, which counts customers only (Stage 5). */}
        {userIsSuperAdmin && (() => {
          const kidsCount = users.filter(u => u.accountType !== 'adult').length;
          const adultsCount = users.filter(u => u.accountType === 'adult').length;
          const segments = [
            { value: 'all',    label: `All (${users.length})` },
            { value: 'kids',   label: `Kids (${kidsCount})` },
            { value: 'adults', label: `Adults (${adultsCount})` },
          ];
          return (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Audience</span>
              <div className="flex gap-1">
                {segments.map(seg => (
                  <button
                    key={seg.value}
                    onClick={() => setAudienceSegment(seg.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      audienceSegment === seg.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {seg.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Summary Cards - Only super admin */}
        {userIsSuperAdmin && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <SummaryCard
              icon={<Users size={20} />}
              label="Total Accounts"
              value={currentStats.totalAccounts}
              subline={currentStats.totalLeadAccounts > 0
                ? `+${currentStats.totalLeadAccounts} trial-only (current, not counted above)`
                : null}
              color="blue"
              tooltip="Accounts with at least one enrolled student (a child who has ever had a course). Trial-only accounts (no course yet, but an open trial) are counted separately in the figure below. Accounts with no students and no open trial — an abandoned signup, or a legacy account with no course at all — are counted in neither, so these two figures will not sum to the account total shown by the Audience toggle above. That's expected: the toggle is a filter control and deliberately counts every registered account, while these two figures count customers and pipeline leads specifically."
            />
            <SummaryCard
              icon={<TrendingUp size={20} />}
              label="Active Learners"
              value={currentStats.activeChildren}
              color="green"
              tooltip="Learners who currently have an active course. This is your real-time count of paying students."
            />
            <SummaryCard
              icon={<Users size={20} />}
              label="New Signups"
              value={currentStats.newSignups}
              color="purple"
              change={previousStats ? computeChange(currentStats.newSignups, previousStats.newSignups) : null}
              comparisonLabel={comparisonLabel}
              tooltip="Parent accounts that signed up and had at least one active child during the selected period. For All Time, this shows all accounts with active children."
            />
            <SummaryCard
              icon={<User size={20} />}
              label="New Signup Children"
              value={currentStats.newSignupChildren}
              color="indigo"
              change={previousStats ? computeChange(currentStats.newSignupChildren, previousStats.newSignupChildren) : null}
              comparisonLabel={comparisonLabel}
              tooltip="Active children who were enrolled at the same time their parent first created an account (within 7 days of account creation)."
            />
            <SummaryCard
              icon={<User size={20} />}
              label="Children Added"
              value={currentStats.newChildrenAdded}
              color="yellow"
              change={previousStats ? computeChange(currentStats.newChildrenAdded, previousStats.newChildrenAdded) : null}
              comparisonLabel={comparisonLabel}
              tooltip="Active children who were added to an existing parent account after the initial signup — for example, a parent enrolling a second child later on."
            />
            <SummaryCard
              icon={<UserX size={20} />}
              label="Deactivated"
              value={lifecyclePeriodCounts.deactivated}
              color="red"
              comparisonLabel={comparisonLabel}
              tooltip="Children deactivated (marked expired, not expected to return) during the selected period. Lifecycle data is collected from 26 May 2026 onward — periods before that date will show 0."
            />
            <SummaryCard
              icon={<Pause size={20} />}
              label="Paused"
              value={lifecyclePeriodCounts.paused}
              color="yellow"
              comparisonLabel={comparisonLabel}
              tooltip="Children paused (marked expired but expected to return) during the selected period. Lifecycle data is collected from 26 May 2026 onward — periods before that date will show 0."
            />
            <SummaryCard
              icon={<Calendar size={20} />}
              label="New Enrollments"
              value={currentStats.newEnrollments}
              color="blue"
              change={previousStats ? computeChange(currentStats.newEnrollments, previousStats.newEnrollments) : null}
              comparisonLabel={comparisonLabel}
              tooltip="The number of course payments made during the selected period, based on each child's last payment date recorded in the database. Includes both new signups and renewals."
            />
            <SummaryCard
              icon={<Activity size={20} />}
              label="Active App Users"
              value={currentStats.activeUsersInRange}
              color="green"
              change={previousStats ? computeChange(currentStats.activeUsersInRange, previousStats.activeUsersInRange) : null}
              comparisonLabel={comparisonLabel}
              tooltip="Parents whose child played at least one game during the selected period. This is updated when a game session is completed — it does not track simple logins."
            />
            <SummaryCard
              icon={<LogIn size={20} />}
              label="Logins"
              value={currentStats.loginUsersInRange}
              color="indigo"
              change={previousStats ? computeChange(currentStats.loginUsersInRange, previousStats.loginUsersInRange) : null}
              comparisonLabel={comparisonLabel}
              tooltip="Parents who actively signed in with their email and password during the selected period. Note: this only tracks logins since this feature was added — historical data before that point is not available."
            />
            {revenueStats && (
              <>
                <SummaryCard
                  icon={<DollarSign size={20} />}
                  label="Revenue"
                  value={`$${Math.round(revenueStats.revenue / 100).toLocaleString()}`}
                  color="green"
                  change={revenueStats.previousRevenue !== null ? computeChange(revenueStats.revenue / 100, revenueStats.previousRevenue / 100) : null}
                  comparisonLabel={comparisonLabel}
                  tooltip="Total payments processed through Stripe during the selected period. This is your most accurate revenue number — it comes directly from Stripe's payment records."
                />
                <SummaryCard
                  icon={<DollarSign size={20} />}
                  label="Payments"
                  value={revenueStats.paymentCount}
                  color="indigo"
                  change={revenueStats.previousPaymentCount !== null ? computeChange(revenueStats.paymentCount, revenueStats.previousPaymentCount) : null}
                  comparisonLabel={comparisonLabel}
                  tooltip="The number of individual payment transactions processed through Stripe in the selected period. Each transaction could cover one or more children."
                />
                <SummaryCard
                  icon={<RefreshCw size={20} />}
                  label="Renewals"
                  value={currentStats.totalRenewals}
                  color="blue"
                  change={previousStats ? computeChange(currentStats.totalRenewals, previousStats.totalRenewals) : null}
                  comparisonLabel={comparisonLabel}
                  tooltip="Children whose course was renewed during the selected period. Based on the child's payment date in the database and whether they have a renewal history."
                />
              </>
            )}
            <SummaryCard
              icon={<DollarSign size={20} />}
              label="Lifetime Revenue"
              value={`$${Math.round(currentStats.totalLifetimeRevenue).toLocaleString()}`}
              color="green"
              tooltip="An estimate of total revenue calculated from individual child payment records stored in the database. This may be lower than the Stripe Revenue figure if some payment records are incomplete or missing."
            />
            <SummaryCard
              icon={<DollarSign size={20} />}
              label="Avg Revenue per Learner"
              value={`$${currentStats.avgRevenuePerChild.toLocaleString()}`}
              color="purple"
              tooltip="Lifetime Revenue divided by the number of learners who have payment records in the database. Learners with missing payment data are excluded from both the total and the count."
            />
          </div>
        )}

        {/* Student Lifecycle Panel */}
        {userIsSuperAdmin && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h3 className="text-base font-bold text-gray-800">Student Lifecycle</h3>
              <span className="text-xs text-gray-400">
                Current snapshot — not affected by the date range
              </span>
            </div>

            {(() => {
              const total = currentStats.totalChildren || 0;
              const active = currentStats.activeChildren || 0;
              const completed = currentStats.completedChildren || 0;
              const pending = currentStats.pendingChildren || 0;
              const paused = currentStats.pausedChildren || 0;
              // Expired EXCLUDING paused, to avoid double-counting
              const expiredOnly = Math.max(0, (currentStats.expiredChildren || 0) - paused);

              const segments = [
                { label: 'Active',    value: active,      color: '#16a34a', bg: 'bg-green-100',  text: 'text-green-700' },
                { label: 'Completed', value: completed,   color: '#2563eb', bg: 'bg-blue-100',   text: 'text-blue-700' },
                { label: 'Paused',    value: paused,      color: '#d97706', bg: 'bg-amber-100',  text: 'text-amber-700' },
                { label: 'Expired',   value: expiredOnly, color: '#9ca3af', bg: 'bg-gray-100',   text: 'text-gray-600' },
                { label: 'Pending',   value: pending,     color: '#a855f7', bg: 'bg-purple-100', text: 'text-purple-700' },
              ];
              const pct = (v) => total > 0 ? (v / total) * 100 : 0;

              return (
                <>
                  {/* Number row */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-800">{total}</div>
                      <div className="text-xs text-gray-500 mt-0.5">Total Students</div>
                      {/* A trial student is a LEAD, not a student — shown here as
                          its own labelled figure, never folded into `total`.
                          "(current)" because this is a live pipeline snapshot —
                          open-trial status has no relationship to the date range
                          selected above, so this number will not move when the
                          range does, and that is correct, not broken. */}
                      {currentStats.totalLeads > 0 && (
                        <div className="text-[11px] text-amber-600 mt-0.5">
                          +{currentStats.totalLeads} open trial lead{currentStats.totalLeads !== 1 ? 's' : ''} (current, not counted above)
                        </div>
                      )}
                    </div>
                    {segments.map(s => (
                      <div key={s.label} className="text-center">
                        <div className={`text-2xl font-bold ${s.text}`}>{s.value}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Stacked bar */}
                  {total > 0 && (
                    <div className="mb-3">
                      <div className="flex h-4 rounded-full overflow-hidden bg-gray-100">
                        {segments.map(s => (
                          s.value > 0 ? (
                            <div
                              key={s.label}
                              style={{ width: `${pct(s.value)}%`, backgroundColor: s.color }}
                              title={`${s.label}: ${s.value} (${pct(s.value).toFixed(1)}%)`}
                            />
                          ) : null
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                        {segments.map(s => (
                          <div key={s.label} className="flex items-center gap-1.5 text-xs text-gray-600">
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-sm"
                              style={{ backgroundColor: s.color }}
                            />
                            {s.label} {total > 0 ? `${pct(s.value).toFixed(0)}%` : ''}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Enrollment split */}
                  <div className="border-t border-gray-100 mt-4 pt-4">
                    <div className="text-xs text-gray-500 mb-2">
                      Of {active} active students — class enrollment:
                    </div>
                    <div className="grid grid-cols-2 gap-3 max-w-sm">
                      <div className="rounded-lg bg-green-50 p-3 text-center">
                        <div className="text-xl font-bold text-green-700">
                          {enrollmentSplit.activeEnrolled}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">Enrolled in a class</div>
                      </div>
                      <div className="rounded-lg bg-amber-50 p-3 text-center">
                        <div className="text-xl font-bold text-amber-700">
                          {enrollmentSplit.activeNotEnrolled}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">Not enrolled</div>
                      </div>
                    </div>
                    {enrollmentSplit.activeNotEnrolled > 0 && (
                      <p className="text-xs text-gray-400 mt-2">
                        Active students not in a class can be managed in the
                        Renewals page under the "Not Enrolled" tab.
                      </p>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Monthly Revenue — super admin only */}
        {userIsSuperAdmin && (
          <div className="grid grid-cols-1 gap-6">
            {/* Monthly Revenue Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Monthly Revenue Trend</h3>
                <span className="text-xs text-gray-400">Platform-wide · all audiences</span>
              </div>
              {paymentLoading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : paymentError ? (
                <div className="text-red-600 text-sm">{paymentError}</div>
              ) : (
                <MonthlyRevenueChart monthlyRevenue={localMonthlyRevenue} />
              )}
            </div>
          </div>
        )}

        {/* Period Detail — who signed up, renewed, or was added (staff + super admin) */}
        {userIsAdmin && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {(() => {
              const peopleTabs = [
                { key: 'signups',       title: 'New Signups',    label: 'Signups',        dateLabel: 'Signed up', list: currentStats.signupChildrenList },
                { key: 'renewals',      title: 'Renewals',       label: 'Renewals',       dateLabel: 'Paid',      list: currentStats.renewalsList },
                { key: 'childrenAdded', title: 'Children Added', label: 'Children Added', dateLabel: 'Added',     list: currentStats.childrenAddedList },
              ];
              const active = peopleTabs.find(t => t.key === activePeopleTab) || peopleTabs[0];
              const rows = active.list || [];
              return (
                <>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">
                    {active.title} ({rows.length})
                  </h3>

                  <div className="flex gap-1 mb-4 border-b border-gray-200">
                    {peopleTabs.map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setActivePeopleTab(tab.key)}
                        className={`pb-2 px-3 text-sm transition-colors ${
                          activePeopleTab === tab.key
                            ? 'text-purple-600 border-b-2 border-purple-600 font-semibold'
                            : 'text-gray-500 hover:text-gray-700 cursor-pointer'
                        }`}
                      >
                        {tab.label} ({(tab.list || []).length})
                      </button>
                    ))}
                  </div>

                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {rows.length === 0 ? (
                      <p className="text-gray-500 text-sm">No {active.label.toLowerCase()} in this period</p>
                    ) : (
                      rows.map(row => (
                        <div key={row.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="min-w-0 mr-3">
                            <div className="font-medium text-gray-800 truncate">{row.childName}</div>
                            <div className="text-sm text-gray-500 truncate">{row.parentName}</div>
                            <div className="text-xs text-gray-400 truncate">{row.parentEmail}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-medium text-gray-800">{getPackageName(row.packageId)}</div>
                            <div className="text-xs text-gray-500">{active.dateLabel} {formatDate(row.date)}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Growth Analytics - Only super admin */}
        {userIsSuperAdmin && (() => {
          // Compute granularity options based on date range span
          const getSpanDays = () => {
            if (selectedDatePreset === 'All Time') return Infinity;
            if (selectedDatePreset === 'Custom') {
              if (!customStart || !customEnd) return 30;
              const s = new Date(customStart + 'T00:00:00');
              const e = new Date(customEnd + 'T23:59:59');
              return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)));
            }
            const preset = DATE_PRESETS.find(p => p.label === selectedDatePreset);
            const range = preset.getRange();
            if (!range.start || !range.end) return Infinity;
            return Math.max(1, Math.round((range.end - range.start) / (1000 * 60 * 60 * 24)));
          };
          const span = getSpanDays();
          const granularityOptions = [
            { value: 'daily',   label: 'Daily',   enabled: span !== Infinity && span <= 90 },
            { value: 'weekly',  label: 'Weekly',  enabled: span !== Infinity && span >= 14 && span <= 365 },
            { value: 'monthly', label: 'Monthly', enabled: span >= 60 },
            { value: 'yearly',  label: 'Yearly',  enabled: span >= 365 },
          ];

          const { signups, renewals, childrenAdded, totals, lifecycle, lifecycleTotals } = growthChartData;
          const angleXAxis = (signups.length > 12);
          const lifecycleTotalCount = lifecycleTotals.new_signup + lifecycleTotals.renewal + lifecycleTotals.completion_job + lifecycleTotals.deactivate + lifecycleTotals.pause;
          const hasNoData = totals.signups === 0 && totals.renewals === 0 && totals.childrenAdded === 0 && lifecycleTotalCount === 0;

          const signupChildrenTotal = signups.reduce((s, b) => s + (b.newChildren || 0), 0);

          const tabs = [
            { key: 'signups',      label: `New Signups (${totals.signups} acct · ${signupChildrenTotal} children)` },
            { key: 'renewals',     label: `Renewals (${totals.renewals})` },
            { key: 'childrenAdded',label: `Children Added (${totals.childrenAdded})` },
            { key: 'lifecycle',    label: `Lifecycle (${lifecycleTotals.new_signup + lifecycleTotals.renewal + lifecycleTotals.completion_job + lifecycleTotals.deactivate + lifecycleTotals.pause})` },
          ];

          return (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              {/* Header row */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Growth Analytics</h3>
                <div className="flex gap-1">
                  {granularityOptions.map(opt => (
                    <button
                      key={opt.value}
                      disabled={!opt.enabled}
                      onClick={() => opt.enabled && setChartGranularity(opt.value)}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                        !opt.enabled
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                          : chartGranularity === opt.value
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab row */}
              <div className="flex gap-1 mb-6 border-b border-gray-200">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveGrowthTab(tab.key)}
                    className={`pb-2 px-4 text-sm transition-colors ${
                      activeGrowthTab === tab.key
                        ? 'text-purple-600 border-b-2 border-purple-600 font-semibold'
                        : 'text-gray-500 hover:text-gray-700 cursor-pointer'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Chart area */}
              {hasNoData ? (
                <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">
                  No data for this period
                </div>
              ) : (
                <>
                  {activeGrowthTab === 'signups' && (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={signups} margin={{ top: 4, right: 16, left: 0, bottom: angleXAxis ? 40 : 4 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          angle={angleXAxis ? -45 : 0}
                          textAnchor={angleXAxis ? 'end' : 'middle'}
                          interval={0}
                        />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                        <RechartsTooltip
                          formatter={(value, name) => [value, name === 'newAccounts' ? 'New Accounts' : 'New Children']}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Legend formatter={name => name === 'newAccounts' ? 'New Accounts' : 'New Children'} wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="newAccounts" name="newAccounts" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="newChildren" name="newChildren" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeGrowthTab === 'renewals' && (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={renewals} margin={{ top: 4, right: 16, left: 0, bottom: angleXAxis ? 40 : 4 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          angle={angleXAxis ? -45 : 0}
                          textAnchor={angleXAxis ? 'end' : 'middle'}
                          interval={0}
                        />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                        <RechartsTooltip
                          formatter={(value) => [value, 'Renewals']}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Bar dataKey="renewals" name="Renewals" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeGrowthTab === 'childrenAdded' && (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={childrenAdded} margin={{ top: 4, right: 16, left: 0, bottom: angleXAxis ? 40 : 4 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          angle={angleXAxis ? -45 : 0}
                          textAnchor={angleXAxis ? 'end' : 'middle'}
                          interval={0}
                        />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                        <RechartsTooltip
                          formatter={(value) => [value, 'Children Added']}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Bar dataKey="childrenAdded" name="Children Added" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {activeGrowthTab === 'lifecycle' && (
                    <>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={lifecycle} margin={{ top: 4, right: 16, left: 0, bottom: angleXAxis ? 40 : 4 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: '#6b7280' }}
                            angle={angleXAxis ? -45 : 0}
                            textAnchor={angleXAxis ? 'end' : 'middle'}
                            interval={0}
                          />
                          <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                          <RechartsTooltip
                            formatter={(value, name) => {
                              const labels = {
                                new_signup: 'New Signups',
                                renewal: 'Renewals',
                                completion_job: 'Completions',
                                deactivate: 'Deactivations',
                                pause: 'Pauses',
                              };
                              return [value, labels[name] || name];
                            }}
                            contentStyle={{ fontSize: 12 }}
                          />
                          <Legend
                            formatter={name => {
                              const labels = {
                                new_signup: 'New Signups',
                                renewal: 'Renewals',
                                completion_job: 'Completions',
                                deactivate: 'Deactivations',
                                pause: 'Pauses',
                              };
                              return labels[name] || name;
                            }}
                            wrapperStyle={{ fontSize: 12 }}
                          />
                          <Bar dataKey="new_signup"     name="new_signup"     fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="renewal"        name="renewal"        fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="completion_job" name="completion_job" fill="#10b981" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="deactivate"     name="deactivate"     fill="#ef4444" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="pause"          name="pause"          fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                      <p className="text-xs text-gray-400 text-center mt-2">
                        Lifecycle data is collected from 26 May 2026 onward. Periods before that date will show no events.
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Engagement Stats - Only super admin */}
        {userIsSuperAdmin && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Platform Engagement</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {Math.round(engagementStats.totalPlayTime / 3600)}h
                </div>
                <div className="text-sm text-gray-600">Total Play Time</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {engagementStats.totalSessions.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total Sessions</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {Math.round(engagementStats.avgPlayTimePerSession / 60)}m
                </div>
                <div className="text-sm text-gray-600">Avg Session Time</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">
                  {engagementStats.avgAccuracy}%
                </div>
                <div className="text-sm text-gray-600">Avg Accuracy</div>
              </div>
            </div>
          </div>
        )}

        {/* Geographic Distribution */}
        <GeographicDistribution
          users={segmentedUsers}
          availableStatuses={availableStatuses}
          statusColor={statusColor}
          openTrialStudentIds={openTrialStudentIds}
        />

        {/* User Table */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">Users ({filteredUsers.length})</h3>
            
            {/* Filters */}
            <div className="flex gap-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search name, email, phone, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Status</option>
                {availableStatuses.map(s => (
                  <option key={s} value={s}>{formatStatusLabel(s)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <SortableHeader 
                    label="Parent" 
                    field="name" 
                    currentField={sortField} 
                    currentDirection={sortDirection} 
                    onSort={handleSort} 
                  />
                  <SortableHeader 
                    label="Email" 
                    field="email" 
                    currentField={sortField} 
                    currentDirection={sortDirection} 
                    onSort={handleSort} 
                  />
                  <SortableHeader 
                    label="Children" 
                    field="children" 
                    currentField={sortField} 
                    currentDirection={sortDirection} 
                    onSort={handleSort} 
                  />
                  <th className="p-2">Status</th>
                  <SortableHeader 
                    label="Created" 
                    field="createdAt" 
                    currentField={sortField} 
                    currentDirection={sortDirection} 
                    onSort={handleSort} 
                  />
                  <SortableHeader 
                    label="Last Active" 
                    field="lastActive" 
                    currentField={sortField} 
                    currentDirection={sortDirection} 
                    onSort={handleSort} 
                  />
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => {
                  const isExpanded = expandedUserId === user.uid;
                  // Tally all statuses present on this user's children, then sort
                  // by STATUS_CONFIG priority for display. Unknown statuses get a
                  // neutral slate pill so they remain visible.
                  const statusCounts = {};
                  let coursedCount = 0;
                  let leadCount = 0;
                  (user.children ?? []).forEach(c => {
                    if (c.course) {
                      coursedCount++;
                      const s = c.course.status;
                      if (s) statusCounts[s] = (statusCounts[s] || 0) + 1;
                      return;
                    }
                    // Same open-trial definition as everywhere else on this
                    // page (see the openTrialStudentIds build in fetchUsers) —
                    // a closed/orphaned/legacy courseless child is neither a
                    // student nor a lead, so it's not shown in this count.
                    if (c.id && openTrialStudentIds.has(c.id)) leadCount++;
                  });
                  const orderedStatuses = Object.entries(statusCounts).sort(([a], [b]) => {
                    const pa = STATUS_CONFIG[a]?.priority ?? UNKNOWN_STATUS_PRIORITY;
                    const pb = STATUS_CONFIG[b]?.priority ?? UNKNOWN_STATUS_PRIORITY;
                    if (pa !== pb) return pa - pb;
                    return a.localeCompare(b);
                  });

                  return (
                    <React.Fragment key={user.uid}>
                      <tr 
                        className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedUserId(isExpanded ? null : user.uid)}
                      >
                        <td className="p-2 font-medium text-gray-800">
                          {user.firstName} {user.lastName}
                        </td>
                        <td className="p-2 text-gray-600">{user.email}</td>
                        <td className="p-2 text-gray-600">
                          {coursedCount}
                          {leadCount > 0 && (
                            <span className="text-amber-600"> (+{leadCount} trial)</span>
                          )}
                        </td>
                        <td className="p-2">
                          <div className="flex gap-1 flex-wrap">
                            {orderedStatuses.map(([status, count]) => (
                              <span
                                key={status}
                                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  STATUS_CONFIG[status]?.pillClass ?? UNKNOWN_STATUS_PILL_CLASS
                                }`}
                              >
                                {count} {status}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-2 text-gray-600">{formatDate(user.createdAt)}</td>
                        <td className="p-2 text-gray-600">
                          {user.analytics?.lastActive ? formatDate(user.analytics.lastActive) : '—'}
                        </td>
                        <td className="p-2">
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </td>
                      </tr>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="7" className="p-4 bg-gray-50">
                            <div className="space-y-4">
                              <div className="flex items-center gap-4 flex-wrap pb-3 mb-3 border-b border-gray-200">
                                <CopyableId label="User ID" value={user.uid} />
                                {user.phoneNumber && (
                                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                    <Phone size={12} className="text-gray-400" />
                                    <a
                                      href={`tel:${(user.phoneNumber || '').replace(/[^\d+]/g, '')}`}
                                      className="text-blue-600 hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {user.phoneNumber}
                                    </a>
                                  </span>
                                )}
                                {(user.city || user.stateProvince || user.country) && (
                                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                    <MapPin size={12} className="text-gray-400" />
                                    {[user.city, user.stateProvince, user.country].filter(Boolean).join(', ')}
                                  </span>
                                )}
                                {(() => {
                                  const tz = resolveTimezone(user.country, user.stateProvince);
                                  const local = formatLocalTime(tz);
                                  return local ? (
                                    <span
                                      className="inline-flex items-center gap-1 text-xs text-gray-500"
                                      title={`Approx. local time · ${tz}`}
                                    >
                                      <Clock size={12} className="text-gray-400" />
                                      {local}
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                              {/* Children Details */}
                              <div>
                                <h4 className="font-medium text-gray-800 mb-2">Children & Courses</h4>
                                <div className="space-y-3">
                                  {user.children.map((child, idx) => (
                                    <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="font-medium text-gray-800 text-base">{child.name}</div>
                                        <CopyableId label="Child ID" value={child.id} />
                                        {child.course && (
                                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            STATUS_CONFIG[child.course.status]?.pillClass ?? UNKNOWN_STATUS_PILL_CLASS
                                          }`}>
                                            {child.course.status}
                                          </span>
                                        )}
                                      </div>
                                      
                                      {child.dateOfBirth && (
                                        <div className="text-sm text-gray-500 mb-3">
                                          DOB: {formatDate(child.dateOfBirth)}
                                        </div>
                                      )}
                                      
                                      {/* Current Course — Stripe info + live class slot */}
                                      {child.course && (
                                        <div className="mb-3 pb-3 border-b border-gray-100">
                                          <div className="text-xs font-semibold text-gray-600 mb-2">CURRENT COURSE</div>
                                          <div className="text-sm text-gray-600 space-y-1">
                                            <div>Package: <span className="font-medium">{getPackageName(child.course.packageId)}</span></div>
                                            <div>Start: {formatDate(child.course.startDate)}</div>
                                            <div>End: {formatDate(child.course.endDate)}</div>
                                            {child.course.earlyRenewalDate && (
                                              <div>Early Renewal: {formatDate(child.course.earlyRenewalDate)}</div>
                                            )}
                                            {userIsSuperAdmin && child.course.paymentAmount && (
                                              <div>Payment: <span className="font-medium">${child.course.paymentAmount}</span></div>
                                            )}
                                          </div>
                                          {/* Class slot(s) this child is enrolled in */}
                                          {(() => {
                                            const DAY_LABELS = {
                                              monday:'Monday', tuesday:'Tuesday', wednesday:'Wednesday',
                                              thursday:'Thursday', friday:'Friday', saturday:'Saturday', sunday:'Sunday'
                                            };
                                            function fmtT(t) {
                                              if (!t) return '';
                                              const [h, m] = t.split(':');
                                              const hr = parseInt(h, 10);
                                              return `${hr % 12 === 0 ? 12 : hr % 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
                                            }
                                            const enrolled = classes.filter(cls =>
                                              cls.status === 'active' &&
                                              (cls.enrolledStudents ?? []).some(
                                                s => s.userId === user.uid && s.childId === child.id
                                              )
                                            );
                                            if (enrolled.length === 0) return (
                                              <p className="text-xs text-gray-400 italic mt-2">Not assigned to a class slot yet.</p>
                                            );
                                            return (
                                              <div className="mt-2 space-y-1">
                                                {enrolled.map(cls => {
                                                  const s = (cls.enrolledStudents ?? []).find(
                                                    s => s.userId === user.uid && s.childId === child.id
                                                  );
                                                  return (
                                                    <div key={cls.id} className="flex items-center gap-2 flex-wrap text-xs bg-purple-50 border border-purple-100 rounded-lg px-3 py-2">
                                                      <span className="font-medium text-purple-800">
                                                        {DAY_LABELS[cls.dayOfWeek] ?? cls.dayOfWeek} at {fmtT(cls.time)}
                                                      </span>
                                                      <span className="text-purple-400">·</span>
                                                      <span className="text-purple-700">{cls.teacherName}</span>
                                                      <span className="text-purple-400">·</span>
                                                      <span className="text-purple-600 capitalize">{cls.type}</span>
                                                      {s && (
                                                        <>
                                                          <span className="text-purple-400">·</span>
                                                          <span className="text-purple-700 font-medium">
                                                            {s.sessionsCompleted ?? 0}/{s.sessionsTotal ?? 6} sessions
                                                          </span>
                                                        </>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      )}

                                      {/* Child Metrics */}
                                      {child.metrics && (
                                        <div className="mb-3 pb-3 border-b border-gray-100">
                                          <div className="text-xs font-semibold text-gray-600 mb-2">LIFETIME METRICS</div>
                                          <div className="grid grid-cols-2 gap-2">
                                            <div className="bg-blue-50 p-2 rounded text-center">
                                              <div className="text-xs text-gray-600">Total Courses</div>
                                              <div className="text-lg font-bold text-blue-700">{child.metrics.totalCourses}</div>
                                            </div>
                                            <div className="bg-green-50 p-2 rounded text-center">
                                              <div className="text-xs text-gray-600">Total Revenue</div>
                                              <div className="text-lg font-bold text-green-700">
                                                ${(child.metrics.totalRevenue).toFixed(0)}
                                              </div>
                                            </div>
                                            <div className="bg-purple-50 p-2 rounded text-center">
                                              <div className="text-xs text-gray-600">Renewal Count</div>
                                              <div className="text-lg font-bold text-purple-700">{child.metrics.renewalCount}</div>
                                            </div>
                                            <div className="bg-yellow-50 p-2 rounded text-center">
                                              <div className="text-xs text-gray-600">Avg Gap</div>
                                              <div className="text-lg font-bold text-yellow-700">
                                                {child.metrics.averageGapDays?.toFixed(0) ?? '—'}d
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                      {/* Course History */}
                                      {child.courseHistory && child.courseHistory.length > 0 && (
                                        <div>
                                          <div className="text-xs font-semibold text-gray-600 mb-2">
                                            COURSE HISTORY ({child.courseHistory.length} past courses)
                                          </div>
                                          <div className="space-y-2">
                                            {child.courseHistory.map((pastCourse, histIdx) => (
                                              <div key={histIdx} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                                <div className="flex items-center justify-between">
                                                  <div>
                                                    <span className="font-medium">{getPackageName(pastCourse.packageId)}</span>
                                                    {userIsSuperAdmin && pastCourse.paymentAmount && (
                                                      <span className="ml-2 text-green-600 font-medium">${pastCourse.paymentAmount}</span>
                                                    )}
                                                  </div>
                                                  <span className="text-xs text-gray-400">
                                                    {formatDate(pastCourse.startDate)} → {formatDate(pastCourse.endDate)}
                                                  </span>
                                                </div>
                                                {pastCourse.archivedReason && (
                                                  <div className="text-xs text-gray-400 mt-1">
                                                    Completed: {pastCourse.archivedReason}
                                                    {pastCourse.daysBetweenCourses !== undefined && (
                                                      <span className="ml-2">• Gap: {pastCourse.daysBetweenCourses} days</span>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {/* Session History */}
                                      {child.id && (
                                        <SessionHistory
                                          childId={child.id}
                                          childName={child.name}
                                          classes={classes}
                                          userId={user.uid}
                                        />
                                      )}

                                      {/* ── TEMPORARY: Class Link ──────────────────────────────
                                           Remove this block when calendar booking is live.
                                      ─────────────────────────────────────────────────────── */}
                                      <div className="mb-3 pb-3 border-b border-gray-100">
                                        <div className="flex items-center gap-1.5 mb-2">
                                          <Link size={12} className="text-blue-500" />
                                          <span className="text-xs font-semibold text-gray-600">CLASS LINK (ZOOM)</span>
                                        </div>
                                        <div className="flex gap-2">
                                          <input
                                            type="url"
                                            placeholder="Paste Zoom / meeting link…"
                                            value={classLinkInputs[child.id] ?? child.classLink ?? ''}
                                            onChange={e => setClassLinkInputs(prev => ({
                                              ...prev,
                                              [child.id]: e.target.value
                                            }))}
                                            className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-700"
                                          />
                                          <button
                                            onClick={() => handleSaveClassLink(user.uid, child.id)}
                                            disabled={savingClassLink === child.id}
                                            className="px-3 py-2 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 whitespace-nowrap"
                                          >
                                            {savingClassLink === child.id ? (
                                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                              'Save'
                                            )}
                                          </button>
                                        </div>
                                        {child.classLink && (
                                          <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                                            <CheckCircle2 size={11} />
                                            Link saved — visible to parent
                                          </p>
                                        )}
                                        {classLinkErrors[child.id] && (
                                          <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                                            <XCircle size={11} />
                                            {classLinkErrors[child.id]}
                                          </p>
                                        )}
                                        {child.classLinkMeetingId && (
                                          <p className="text-xs text-gray-400 mt-1.5">Meeting ID: {child.classLinkMeetingId}</p>
                                        )}
                                        {child.classLinkPasscode && (
                                          <p className="text-xs text-gray-400">Passcode: {child.classLinkPasscode}</p>
                                        )}
                                      </div>

                                      {/* Report Cards Section - Admin View */}
                                      <div className="mb-3 pb-3 border-b border-gray-100">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="text-xs font-semibold text-gray-600">
                                            REPORT CARDS {child.reportCards && `(${child.reportCards.length})`}
                                          </div>
                                          <button
                                            onClick={() => handleOpenUploadModal(user, child)}
                                            className="text-xs bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 flex items-center gap-1"
                                          >
                                            <Upload size={12} />
                                            Upload
                                          </button>
                                        </div>
                                        
                                        {child.reportCards && child.reportCards.length > 0 ? (
                                          <div className="space-y-2">
                                            {child.reportCards
                                              .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
                                              .map((rc, rcIdx) => (
                                                <div key={rcIdx} className="text-xs bg-gray-50 p-3 rounded-lg">
                                                  <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1">
                                                      <div className="flex items-center gap-2 mb-1">
                                                        <FileText size={14} className="text-blue-600" />
                                                        <span className="font-medium text-gray-800">
                                                          {rc.reportTitle}
                                                        </span>
                                                      </div>
                                                      <div className="text-xs text-gray-500 space-y-0.5">
                                                        <div>Uploaded: {formatDate(rc.uploadedAt)}</div>
                                                        <div>By: {rc.uploadedByName || 'Admin'}</div>
                                                        {rc.notes && (
                                                          <div className="mt-1 text-gray-600 italic">
                                                            "{rc.notes}"
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                    <div className="flex gap-2 items-center">
                                                      <a
                                                        href={rc.fileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
                                                      >
                                                        <FileText size={14} />
                                                        <span>View</span>
                                                      </a>
                                                      <button
                                                        onClick={() => handleDeleteReportCard(user.uid, child.id, rc.id)}
                                                        disabled={deletingReportCard === rc.id}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                      >
                                                        {deletingReportCard === rc.id ? (
                                                          <>
                                                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            <span>Deleting...</span>
                                                          </>
                                                        ) : (
                                                          <>
                                                            <Trash2 size={14} />
                                                            <span>Delete</span>
                                                          </>
                                                        )}
                                                      </button>
                                                    </div>
                                                  </div>
                                                </div>
                                              ))}
                                          </div>
                                        ) : (
                                          <div className="text-xs text-gray-500 italic">No report cards uploaded yet</div>
                                        )}
                                      </div>                                      
                                      
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Analytics */}
                              {user.analytics && (
                                <div>
                                  <h4 className="font-medium text-gray-800 mb-2">Analytics</h4>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                                      <div className="text-xs text-gray-500">Play Time</div>
                                      <div className="font-medium text-gray-800">
                                        {Math.round((user.analytics.totalPlayTime || 0) / 3600)}h
                                      </div>
                                    </div>
                                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                                      <div className="text-xs text-gray-500">Sessions</div>
                                      <div className="font-medium text-gray-800">
                                        {user.analytics.sessionsCount || 0}
                                      </div>
                                    </div>
                                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                                      <div className="text-xs text-gray-500">Total Score</div>
                                      <div className="font-medium text-gray-800">
                                        {(user.analytics.totalScore || 0).toLocaleString()}
                                      </div>
                                    </div>
                                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                                      <div className="text-xs text-gray-500">Accuracy</div>
                                      <div className="font-medium text-gray-800">
                                        {user.analytics.totalAttempts > 0
                                          ? ((user.analytics.totalCorrect / user.analytics.totalAttempts) * 100).toFixed(1)
                                          : 0}%
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Payment Info - Only super admin */}
                              {userIsSuperAdmin && user.payment && (
                                <div>
                                  <h4 className="font-medium text-gray-800 mb-2">Payment Info</h4>
                                  <div className="p-3 bg-white rounded-lg border border-gray-200 text-sm text-gray-600">
                                    <div>Stripe Customer: {user.payment.stripeCustomerId || '—'}</div>
                                    <div>Saved Payment Method: {user.payment.savedPaymentMethodId ? 'Yes' : 'No'}</div>
                                    <div>Last Quantity: {user.payment.lastQuantity || '—'}</div>
                                  </div>
                                </div>
                              )}

                              {/* Game Progress */}
                              {user.progress && Object.keys(user.progress).length > 0 && (
                                <div>
                                  <h4 className="font-medium text-gray-800 mb-2">Game Progress</h4>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {Object.entries(user.progress).map(([gameKey, data]) => (
                                      <div key={gameKey} className="p-2 bg-white rounded-lg border border-gray-200 text-sm">
                                        <div className="font-medium text-gray-800 text-xs mb-1">
                                          {getGameName(gameKey)}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                          Level {data.currentLevel || 0}/{data.maxLevel || 0}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Account Management - Super Admin Only */}
                              {userIsSuperAdmin && (
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                  <h4 className="font-medium text-gray-800 mb-3">Account Management</h4>
                                  <div className="flex gap-2">
                                    {/* Button 1: Send Reset Email */}
                                    <div>
                                      <button
                                        disabled={passwordResetStatus[user.uid]?.loading}
                                        onClick={async () => {
                                          setPasswordResetStatus(prev => ({ ...prev, [user.uid]: { loading: true } }));
                                          try {
                                            await sendPasswordResetEmail(auth, user.email);
                                            setPasswordResetStatus(prev => ({ ...prev, [user.uid]: { successMsg: 'Reset email sent!' } }));
                                          } catch (err) {
                                            setPasswordResetStatus(prev => ({ ...prev, [user.uid]: { errorMsg: err.message } }));
                                          }
                                        }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {passwordResetStatus[user.uid]?.loading ? (
                                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <Mail size={12} />
                                        )}
                                        Send Reset Email
                                      </button>
                                      {passwordResetStatus[user.uid]?.successMsg && (
                                        <p className="text-xs text-green-600 mt-1">{passwordResetStatus[user.uid].successMsg}</p>
                                      )}
                                      {passwordResetStatus[user.uid]?.errorMsg && (
                                        <p className="text-xs text-red-600 mt-1">{passwordResetStatus[user.uid].errorMsg}</p>
                                      )}
                                    </div>

                                    {/* Button 2: Set Temp Password */}
                                    <div>
                                      {!tempPasswordState[user.uid]?.showing ? (
                                        <button
                                          onClick={() => {
                                            const digits = Math.floor(1000 + Math.random() * 9000);
                                            const generated = `Roots-${digits}`;
                                            setTempPasswordState(prev => ({
                                              ...prev,
                                              [user.uid]: { showing: true, generatedPassword: generated },
                                            }));
                                          }}
                                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-medium rounded-lg hover:bg-orange-600"
                                        >
                                          <KeyRound size={12} />
                                          Set Temp Password
                                        </button>
                                      ) : (
                                        <div className="bg-white border border-orange-200 rounded-lg p-3 text-xs space-y-2 min-w-[260px]">
                                          <div className="text-gray-700 font-medium">{user.displayName || user.email}</div>
                                          <div className="text-gray-500">{user.email}</div>
                                          <div className="flex items-center gap-2">
                                            <span className="font-mono bg-gray-100 px-2 py-1 rounded text-gray-800 tracking-wider">
                                              {tempPasswordState[user.uid].generatedPassword}
                                            </span>
                                            <button
                                              onClick={() => navigator.clipboard.writeText(tempPasswordState[user.uid].generatedPassword)}
                                              className="text-blue-500 hover:text-blue-700 underline"
                                            >
                                              Copy
                                            </button>
                                          </div>
                                          <div className="flex gap-2 pt-1">
                                            <button
                                              disabled={tempPasswordState[user.uid]?.confirming}
                                              onClick={async () => {
                                                setTempPasswordState(prev => ({ ...prev, [user.uid]: { ...prev[user.uid], confirming: true, confirmError: null, confirmSuccess: null } }));
                                                try {
                                                  const fns = getFunctions();
                                                  const resetFn = httpsCallable(fns, 'adminResetUserPassword');
                                                  await resetFn({ email: user.email, newPassword: tempPasswordState[user.uid].generatedPassword });
                                                  setTempPasswordState(prev => ({ ...prev, [user.uid]: { ...prev[user.uid], confirming: false, confirmSuccess: 'Password updated!' } }));
                                                } catch (err) {
                                                  setTempPasswordState(prev => ({ ...prev, [user.uid]: { ...prev[user.uid], confirming: false, confirmError: err.message } }));
                                                }
                                              }}
                                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {tempPasswordState[user.uid]?.confirming ? (
                                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                              ) : 'Confirm & Set Password'}
                                            </button>
                                            <button
                                              onClick={() => setTempPasswordState(prev => ({ ...prev, [user.uid]: undefined }))}
                                              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                          {tempPasswordState[user.uid]?.confirmSuccess && (
                                            <p className="text-green-600">{tempPasswordState[user.uid].confirmSuccess}</p>
                                          )}
                                          {tempPasswordState[user.uid]?.confirmError && (
                                            <p className="text-red-600">{tempPasswordState[user.uid].confirmError}</p>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    {/* Button 3: View as user (Super Admin impersonation) */}
                                    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                                      <button
                                        onClick={() => handleImpersonate(user.uid)}
                                        className="px-3 py-1 text-sm font-semibold rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                                        title="Generate an incognito login link to view this account"
                                      >
                                        👁️ View as user
                                      </button>
                                      {impersonateStatus[user.uid] && (
                                        <span className="text-xs text-gray-600">{impersonateStatus[user.uid]}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
            </table>
          </div>
        </div>
        
        {/* Report Card Upload Modal */}
        <ReportCardUploadModal
          isOpen={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          child={selectedChildForUpload}
          userId={selectedUserForUpload?.uid}
          onUploadSuccess={handleUploadSuccess}
        />
      </div>
    </div>
  );
}

export default AdminDashboard;